  <template>
    <div class="ai-dialogue">
      <!-- Two Column Layout -->
      <div class="dialogue-content">
        <!-- Left Column: Task Management -->
        <div class="results-preview" v-if="!hide_tabs">
          <!-- Tab Bar -->
          <el-tabs v-model="activeTab" class="result-tabs">
            <el-tab-pane label="Task List" name="tasks">
              <!-- TaskList Component -->
              <TaskList 
                v-if="!selectedTask"
                :tasks="tasks"
                :selectedTaskId="selectedTask ? selectedTask.id : null"
                @task-selected="handleTaskSelected"
              />
              
              <!-- Task Detail Component -->
              <Task 
                v-if="selectedTask"
                :task="selectedTask"
                @back="handleBackToTaskList"
                @task-updated="handleTaskUpdated"
              />
            </el-tab-pane>
            
            <el-tab-pane label="Workflows" name="workflows">
              <!-- Sub Tab Bar for Workflows -->
              <div class="workflow-sub-tabs">
                <el-radio-group v-model="workflowSubTab" size="small" class="workflow-radio-group">
                  <el-radio-button label="my">My Workflows</el-radio-button>
                  <el-radio-button label="public">Public Workflows</el-radio-button>
                </el-radio-group>
              </div>
              
              <!-- Workflow List -->
              <div class="workflow-list">
                <div v-if="loadingWorkflows" class="loading-workflows">
                  <i class="el-icon-loading"></i>
                  Loading workflows...
                </div>
                
                <div v-else-if="currentWorkflows.length === 0" class="empty-workflows">
                  <i class="el-icon-connection"></i>
                  <p>No {{ workflowSubTab === 'my' ? 'personal' : 'public' }} workflows available</p>
                </div>
                
                <div v-else class="workflow-grid">
                  <div 
                    v-for="workflow in currentWorkflows" 
                    :key="workflow._id ? (workflow._id.$oid || workflow._id.$id) : workflow.id"
                    class="workflow-card"
                    @click="selectWorkflow(workflow)"
                    :class="{ 'selected': isWorkflowSelected(workflow) }"
                  >
                    <div class="workflow-icon">
                      <i class="el-icon-connection"></i>
                    </div>
                    <div class="workflow-info">
                      <div class="workflow-header">
                        <h4>{{ workflow.name || 'Unnamed Workflow' }}</h4>
                        <el-tag 
                          v-if="workflow.store_public === 'YES'" 
                          type="success" 
                          size="mini"
                          class="workflow-visibility-badge"
                        >
                          <i class="el-icon-view"></i> Public
                        </el-tag>
                        <el-tag 
                          v-else
                          type="info" 
                          size="mini"
                          class="workflow-visibility-badge"
                        >
                          <i class="el-icon-lock"></i> Private
                        </el-tag>
                      </div>
                      <p>{{ workflow.description || 'No description' }}</p>
                      <div class="workflow-meta">
                        <span class="effect-count">
                          <i class="el-icon-link"></i>
                          {{ getEffectCount(workflow) }} steps
                        </span>
                      </div>
                    </div>
                    <div class="workflow-actions">
                      <el-button 
                        v-if="!isWorkflowInCart(workflow)"
                        type="primary" 
                        size="mini" 
                        @click.stop="addToWorkflowCart(workflow)"
                        icon="el-icon-plus"
                      >
                        Add to Cart
                      </el-button>
                      <el-button 
                        v-else
                        type="success" 
                        size="mini" 
                        @click.stop="removeWorkflowFromCart(workflow)"
                        icon="el-icon-check"
                      >
                        In Cart
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>

        <!-- Right Column: Current Dialogue View -->
        <div class="dialogue-section" :class="{ 'full-width': hide_tabs }">
          <div class="dialogue-header">
            <h3>{{ getDialogueTitle() }}</h3>
            
            
            
          </div>

          <div class="messages-container" ref="messagesContainer">
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="message"
              :class="message.role"
            >
              <!-- Show Post component if message has showAsPost flag -->
              <div v-if="message.showAsPost && message.postData" class="post-message">
                <Post 
                  :post="message.postData"
                  :groupOptions="[]"
                  scene="ai-result"
                  :allowPageTurning="true"
                  :disableNavigation="true"
                />
              </div>
              <!-- Regular message content -->
              <div v-else class="message-content" v-html="formatMessageContent(message)"></div>
              
              <!-- Midjourney Image Display -->
              <div v-if="message.type === 'createCollectionCoverPrompt'" class="midjourney-image">
                <img v-if="message.imgUrl" style="max-width: 100%; border-radius: 8px; margin: 10px 0;" :src="message.imgUrl" />
                
                <!-- Image Edit Buttons for non-upscaled images -->
                <div v-if="message.taskId && message.action !== 'UPSCALE'" class="image-edit-buttons">
                  <div class="button-row">
                    <el-button size="small" @click="toEditImage('UPSCALE', 1, message.taskId)">U1</el-button>
                    <el-button size="small" @click="toEditImage('UPSCALE', 2, message.taskId)">U2</el-button>
                    <el-button size="small" @click="toEditImage('UPSCALE', 3, message.taskId)">U3</el-button>
                    <el-button size="small" @click="toEditImage('UPSCALE', 4, message.taskId)">U4</el-button>
                    <el-button size="small" @click="toEditImage('REROLL', null, message.taskId)">
                      <i class="el-icon-refresh"></i>
                    </el-button>
                  </div>
                  <div class="button-row">
                    <el-button size="small" @click="toEditImage('VARIATION', 1, message.taskId)">V1</el-button>
                    <el-button size="small" @click="toEditImage('VARIATION', 2, message.taskId)">V2</el-button>
                    <el-button size="small" @click="toEditImage('VARIATION', 3, message.taskId)">V3</el-button>
                    <el-button size="small" @click="toEditImage('VARIATION', 4, message.taskId)">V4</el-button>
                  </div>
                </div>
                
                <!-- Use as Cover button for upscaled images -->
                <div v-if="message.taskId && message.action === 'UPSCALE'" class="cover-action">
                  <el-button size="small" type="primary" @click="useImageAsCover(message.imgUrl)">
                    Use this image as cover
                  </el-button>
                </div>

                <!-- Regenerate button for Gemini images (no taskId means it's from Gemini) -->
                <div v-if="message.imgUrl && !message.taskId" class="gemini-image-actions">
                  <el-button size="small" type="warning" @click="regenerateGeminiImage(message)" :loading="message.regenerating">
                    <i class="el-icon-refresh-right"></i>
                    Regenerate the image
                  </el-button>
                </div>
              </div>
              
              <!-- Action buttons for AI responses with results -->
              <div
                v-if="
                  message.role === 'assistant' &&
                  !isLoading &&
                  (message.type || message.showAsPost || shouldShowActionButtons(message))
                "
                class="assistant-actions"
              >
                <!-- Collection Description Polish -->
                <el-button
                  v-if="(message.type || getActionButtonType(message)) === 'collectionDescriptionPolish'"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.structure || message.content, 'collectionDescriptionPolish')"
                >
                  Apply this description
                </el-button>

                <!-- Bundle Description Generation -->
                <el-button
                  v-if="(message.type || getActionButtonType(message)) === 'bundleDescriptionGeneration'"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.structure || message.content, 'bundleDescriptionGeneration')"
                >
                  Apply this description
                </el-button>

                <!-- Collection Structure Generation -->
                <el-button
                  v-if="(message.type || getActionButtonType(message)) === 'createCollectionStructure'"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.structure || message.content, 'createCollectionStructure')"
                >
                  Apply this structure to my collection
                </el-button>

                <!-- Group Knowits Suggestion -->
                <el-button
                  v-if="(message.type || getActionButtonType(message)) === 'groupKnowitsSuggestion'"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.structure || message.content, 'groupKnowitsSuggestion')"
                >
                  Apply these knowits to my group
                </el-button>

                <!-- Post Content Generation -->
                <el-button
                  v-if="(message.type || getActionButtonType(message)) === 'createPostContent'"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.structure || message.content, 'createPostContent')"
                >
                  Apply these contents to my post
                </el-button>

                <!-- Single Post Content -->
                <el-button
                  v-if="(message.type || getActionButtonType(message)) === 'createSinglePostContent'"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.structure || message.content, 'createSinglePostContent')"
                >
                  Apply this content to my post
                </el-button>

                <!-- Post Content Improvement -->
                <div v-if="(message.type || getActionButtonType(message)) === 'improveSinglePostContent'" class="improvement-actions">
                  <el-button
                    size="mini"
                    type="primary"
                    @click="takeAIResult(message.structure || message.content, 'update')"
                  >
                    Update this content to my post
                  </el-button>
                  <el-button
                    size="mini"
                    type="primary"
                    @click="takeAIResult(message.structure || message.content, 'insert')"
                  >
                    Insert this content after
                  </el-button>
                </div>

                <!-- Collection Cover Generation -->
                <el-button
                  v-if="(message.type || getActionButtonType(message)) === 'createCollectionCoverPrompt'"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.imgUrl || message.structure || message.content, 'createCollectionCoverPrompt')"
                >
                  Use this image as cover
                </el-button>

                <!-- Create Private Post for output_form 68baff0c94c784e2568b45e7 -->
                <el-button
                  v-if="message.showAsPost && message.postData"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.postData, 'createPrivatePost')"
                >
                  Use this result
                </el-button>

                <!-- General action buttons for other cases -->
                <el-button
                  v-if="!message.showAsPost && (getActionButtonType(message) === 'general' || (!message.type && shouldShowActionButtons(message)))"
                  size="mini"
                  type="primary"
                  @click="takeAIResult(message.structure || message.content, 'general')"
                >
                  Use this result
                </el-button>
              </div>
            </div>
            <div v-if="isLoading" class="message assistant">
              <div class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Context Pot Cart View -->
          <div v-if="globalContextPot" class="context-cart" :class="{ 'empty-cart': !globalContextPot.length }">
            <div class="cart-header">
              <h4>
                <i class="el-icon-shopping-cart-full"></i>
                Context Items ({{ globalContextPot.length }})
              </h4>
              <div class="cart-header-actions">
                <!-- Processing Mode Selection (only show when there are items) -->
                <el-radio-group
                  v-if="globalContextPot.length > 0"
                  v-model="contextProcessingMode"
                  size="mini"
                  class="processing-mode-selector"
                >
                  <el-radio-button label="combined">
                    <el-tooltip content="Process all context items together as a whole for one task" placement="top">
                      <span><i class="el-icon-files"></i> Combined</span>
                    </el-tooltip>
                  </el-radio-button>
                  <el-radio-button label="batch">
                    <el-tooltip content="Process each context item separately, creating identical workflows for batch processing" placement="top">
                      <span><i class="el-icon-document-copy"></i> Batch</span>
                    </el-tooltip>
                  </el-radio-button>
                </el-radio-group>

                <!-- Only show clear button when there are items -->
                <el-button
                  v-if="globalContextPot.length > 0"
                  type="text"
                  size="mini"
                  @click="clearContextPot"
                  class="clear-cart-btn"
                >
                  <i class="el-icon-delete"></i>
                  Clear All
                </el-button>
              </div>
            </div>
            <!-- Show items when cart has content -->
            <div v-if="globalContextPot.length > 0" class="cart-items">
              <div 
                v-for="(item, index) in globalContextPot" 
                :key="index"
                class="cart-item"
              >
                <div class="item-content">
                  <div class="item-label">{{ getItemLabel(item) }}</div>
                  <div class="item-preview">{{ getItemPreview(item) }}</div>
                </div>
                <el-button 
                  type="text" 
                  size="mini" 
                  @click="removeFromPot(index)"
                  class="remove-item-btn"
                >
                  <i class="el-icon-close"></i>
                </el-button>
              </div>
            </div>
            <!-- Show empty state when cart is empty -->
            <div v-else class="empty-cart-message">
              <i class="el-icon-info"></i>
              <span>No context items added yet</span>
            </div>
          </div>

          <!-- Workflow Cart View -->
          <div v-if="workflowCart.length > 0" class="workflow-cart">
            <div class="cart-header">
              <h4>
                <i class="el-icon-s-operation"></i>
                Workflow Cart ({{ workflowCart.length }})
              </h4>
              <div class="cart-header-actions">
                <el-button 
                  type="primary" 
                  size="mini" 
                  @click="executeAllWorkflowsInCart"
                  icon="el-icon-video-play"
                  plain
                >
                  Run All
                </el-button>
                <el-button 
                  type="text" 
                  size="mini" 
                  @click="clearWorkflowCart"
                  class="clear-cart-btn"
                >
                  <i class="el-icon-delete"></i>
                  Clear All
                </el-button>
              </div>
            </div>
            <div class="cart-items">
              <div 
                v-for="(workflow, index) in workflowCart" 
                :key="index"
                class="workflow-cart-item"
              >
                <div class="workflow-cart-content">
                  <div class="workflow-cart-icon">
                    <i class="el-icon-connection"></i>
                  </div>
                  <div class="workflow-cart-info">
                    <div class="workflow-cart-name">{{ workflow.name || 'Unnamed Workflow' }}</div>
                    <div class="workflow-cart-meta">
                      <span>{{ getEffectCount(workflow) }} steps</span>
                    </div>
                  </div>
                </div>
                <div class="workflow-cart-actions">
                  <el-button 
                    type="text" 
                    size="mini" 
                    @click="removeWorkflowFromCart(index)"
                    class="remove-item-btn"
                  >
                    <i class="el-icon-close"></i>
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <div class="input-area">
            <el-input
              v-model="userInput"
              type="textarea"
              :rows="3"
              placeholder="Ask me anything..."
              @keyup.enter.native="handleEnter"
            ></el-input>
            <el-button
              type="primary"
              :disabled="!canSendMessage"
              @click="sendMessage"
              >Send</el-button
            >
          </div>
        </div>
      </div>
    </div>
  </template>
    
  <script>
  import { fetchEventSource } from "@microsoft/fetch-event-source";
  import { postRequest, postRequestAISG } from '@/apis/front';
  import TaskList from './TaskList.vue';
  import Task from './Task.vue';
  import Post from './Post.vue';
  import { getUserId } from '@/utils/store';

  export default {
    name: "UserAIDialogue",
    components: {
      TaskList,
      Task,
      Post
    },
    props: {
      context_string: {
        type: String,
        default: ''
      },
      // context_pot prop is now deprecated - using global store instead
      // Keeping for backward compatibility but not used internally
      context_pot: {
        type: Array,
        default: () => []
      },
      current_chain_id: {
        type: String,
        default: ''
      },
      current_task_id: {
        type: String,
        default: ''
      },
      task_type: {
        type: String,
        default: 'chat'
      },
      hide_tabs: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        messages: [],
        userInput: "",
        isLoading: false,
        currentResponse: "",
        currentMode: "gpt-4",
        selectedTask: null,
        selectedTaskOperation: null,
       
        taskType: "Collection Cover Generation", // Add missing taskType property
        tasks: [
          
        ],

        current_chain:{},
        localTaskId: '',
        loadingMessageIndex: -1,
        loadingDotsInterval: null,
        waitingForChainConfirmation: false,
        pendingTaskOperation: null,
        progress: "0%",
        
        // New properties for workflows
        activeTab: 'tasks',
        workflows: [],
        myWorkflows: [],
        publicWorkflows: [],
        workflowSubTab: 'my',
        workflowCart: [],
        selectedWorkflow: null,
        loadingWorkflows: false,

        // Context processing mode
        contextProcessingMode: 'combined' // 'combined' or 'batch'
      };
    },
    computed: {
      globalContextPot() {
        return this.$store.state.contextPot.items;
      },
      canSendMessage() {
        // Allow sending if:
        // 1. There's user input (trimmed), OR
        // 2. There are items in context pot AND workflows in cart
        const hasUserInput = this.userInput.trim().length > 0;
        const hasContextAndWorkflows = this.globalContextPot && this.globalContextPot.length > 0 && 
                                      this.workflowCart && this.workflowCart.length > 0;
        
        return !this.isLoading && (hasUserInput || hasContextAndWorkflows);
      },
      currentTaskType() {
        if (this.selectedTask) {
          // Map task titles to task types
          const taskTypeMap = {

            'Collection Cover Generation': 'Collection Cover Generation'
          };
          return taskTypeMap[this.selectedTask.title] || 'General AI Assistant';
        }
        return this.taskType;
      },
      currentWorkflows() {
        return this.workflowSubTab === 'my' ? this.myWorkflows : this.publicWorkflows;
      }
    },
    async mounted() {
        try {
          console.log('UserAIDialogue mounted with props:', {
            context_string: this.context_string,
            task_type: this.task_type,
            current_task_id: this.current_task_id
          });
          
          // Initialize local task ID from prop
          this.localTaskId = this.current_task_id;
          
          // Check if this is an AI task execution delegation
          if (this.context_string && this.task_type === 'AI_TASK_EXECUTION') {
            console.log('AI Task Execution detected!');
            try {
              const context = JSON.parse(this.context_string);
              console.log('Parsed AI task context:', context);
              
              // Handle AI task delegation
              if (context.task_type === 'AI_TASK_EXECUTION') {
                console.log('Processing AI delegated task...');
                // Set the task ID for operations
                this.localTaskId = context.task_id;
                
                // Don't add the initial system message or user prompt to display
                // They will be sent to AI but not shown to user
                
                // Automatically trigger AI processing
                this.$nextTick(() => {
                  console.log('Calling processAIDelegatedTask...');
                  this.processAIDelegatedTask(context);
                });
              }
            } catch (e) {
              console.error('Error parsing AI task context:', e);
            }
          } else {
            console.log('Not an AI task delegation - normal mode');
          }
          
          // Sync context_pot prop with global store if provided
          if (this.context_pot && this.context_pot.length > 0) {
            // Add items from prop to global store if not already there
            this.context_pot.forEach(item => {
              this.$store.dispatch('contextPot/addItem', item);
            });
          }

          const myTasks = await postRequest('getTasks',{
            user_id: getUserId()
          });

          if(myTasks && myTasks.data && myTasks.data.code === 0) {
            this.tasks = myTasks.data.data;
          }
          
          // Load workflows
          await this.loadWorkflows();


          if (this.current_chain_id && this.current_chain_id !== '') {
            const chainResponse = await postRequest('getChain', {
              id: this.current_chain_id
            });
            
            if (chainResponse && chainResponse.data && chainResponse.data.code === 0) {
              this.current_chain = chainResponse.data.data;
              

              if (this.localTaskId === '') {
                // Need to build a new task
                // Create task title by concatenating context content name and workflow name
                let taskTitle = this.current_chain.name;
                
                // Check if we have workflows in cart to use the first one's name
                if (this.workflowCart && this.workflowCart.length > 0) {
                  const firstWorkflow = this.workflowCart[0];
                  
                  // Extract context content name if available
                  let contextName = '';
                  if (this.globalContextPot && this.globalContextPot.length > 0) {
                    const firstContext = this.globalContextPot[0];
                    if (typeof firstContext === 'string') {
                      try {
                        const parsed = JSON.parse(firstContext);
                        contextName = parsed.collection_name || parsed.name || parsed.title || '';
                      } catch (e) {
                        contextName = '';
                      }
                    } else {
                      contextName = firstContext.collection_name || firstContext.name || firstContext.title || '';
                    }
                  } else if (this.context_string) {
                    try {
                      const parsed = JSON.parse(this.context_string);
                      contextName = parsed.collection_name || parsed.name || parsed.title || '';
                    } catch (e) {
                      contextName = '';
                    }
                  }
                  
                  // Concatenate context name and first workflow name
                  if (contextName) {
                    taskTitle = `${contextName} - ${firstWorkflow.name || 'Workflow'}`;
                  } else {
                    taskTitle = firstWorkflow.name || 'Workflow Task';
                  }
                } else {
                  // Single chain scenario - extract context name if available
                  let contextName = '';
                  if (this.globalContextPot && this.globalContextPot.length > 0) {
                    const firstContext = this.globalContextPot[0];
                    if (typeof firstContext === 'string') {
                      try {
                        const parsed = JSON.parse(firstContext);
                        contextName = parsed.collection_name || parsed.name || parsed.title || '';
                      } catch (e) {
                        contextName = '';
                      }
                    } else {
                      contextName = firstContext.collection_name || firstContext.name || firstContext.title || '';
                    }
                  } else if (this.context_string) {
                    try {
                      const parsed = JSON.parse(this.context_string);
                      contextName = parsed.collection_name || parsed.name || parsed.title || '';
                    } catch (e) {
                      contextName = '';
                    }
                  }
                  
                  if (contextName) {
                    taskTitle = `${contextName} - ${this.current_chain.name}`;
                  }
                }
                
                const taskResponse = await postRequest('updateTask', {
                  id: this.localTaskId,
                  user_id: getUserId(),
                  title: taskTitle,
                  description: '',
                  operations:[
                    
                  ]
                });
                
                if (taskResponse && taskResponse.data && taskResponse.data.code === 0) {

                  this.tasks.push(taskResponse.data.data);

                  this.localTaskId = (taskResponse.data.data._id.$oid || taskResponse.data.data._id.$id);

                  // Ensure we have context before creating the operation
                  const contextString = this.getContextString();
                  console.log('Creating operation with context:', contextString);
                  
                  const operationResponse = await postRequest('updateTaskOperation',{
                    task_id: this.localTaskId,
                    operation_type: 'chain',
                    chain_id: this.current_chain_id,
                    operation_name: this.current_chain.name,
                    user_id: getUserId(),
                    input_string: contextString,
                    status: 'pending'
                  })

                  if (operationResponse && operationResponse.data && operationResponse.data.code === 0) {

                    console.log('operationResponse full structure:', operationResponse.data.data);
                    this.selectedTaskOperation = operationResponse.data.data;

                    this.tasks[this.tasks.length - 1].operations = [operationResponse.data.data];

                    this.queryAIChain(this.selectedTaskOperation);
                  }
                }
              }
              
            }


            
          }

          
        } catch (error) {
          console.error('Error in mounted:', error);
        }
      },
    
    methods: {
      formatCompleteResponse(responseText) {
        // Format the complete AI response, especially handling subtasks
        if (!responseText) return '';
        
        // Clean up response - remove any markdown code block formatting
        let cleanedResponse = responseText
          .replace(/^```json\s*/i, '')
          .replace(/^```\s*/i, '')
          .replace(/\s*```$/i, '')
          .trim();
        
        // Try to detect and format subtasks in the response
        try {
          // First check if entire response is a JSON array (most common for task delegation)
          if (cleanedResponse.startsWith('[') && cleanedResponse.endsWith(']')) {
            try {
              const subtasksArray = JSON.parse(cleanedResponse);
              if (Array.isArray(subtasksArray) && subtasksArray.length > 0) {
                
                // Build a beautiful formatted HTML for subtasks
                let formattedHtml = '<div class="ai-task-analysis">';
                formattedHtml += '<div class="analysis-header">';
                formattedHtml += '<h3>🤖 AI Task Analysis Complete</h3>';
                formattedHtml += `<p>I've broken down your task into ${subtasksArray.length} actionable subtasks:</p>`;
                formattedHtml += '</div>';
                
                formattedHtml += '<div class="subtasks-container">';
                
                subtasksArray.forEach((task, index) => {
                  const status = task.completed || task.status === 'completed' ? '✅' : '📝';
                  const priority = task.priority || (index + 1);
                  const title = task.title || task.name || `Subtask ${index + 1}`;
                  const description = task.description || '';
                  const time = task.estimated_time || 'Time TBD';
                  const dependencies = task.dependencies && task.dependencies.length > 0 
                    ? task.dependencies.join(', ') 
                    : 'None';
                  
                  formattedHtml += `
                    <div class="subtask-card">
                      <div class="subtask-header">
                        <span class="subtask-number">${priority}.</span>
                        <span class="subtask-status">${status}</span>
                        <span class="subtask-title">${title}</span>
                        <span class="subtask-time">⏱ ${time}</span>
                      </div>
                      ${description ? `<div class="subtask-description">${description}</div>` : ''}
                      ${task.dependencies && task.dependencies.length > 0 ? 
                        `<div class="subtask-dependencies">📌 Depends on: ${dependencies}</div>` : ''}
                    </div>`;
                });
                
                formattedHtml += '</div>';
                
                // Add action buttons
                formattedHtml += '<div class="subtask-actions">';
                formattedHtml += '<button class="action-btn primary" onclick="window.addSubtasksToTask && window.addSubtasksToTask()">➕ Add All Subtasks to Task</button>';
                formattedHtml += '<button class="action-btn secondary" onclick="window.executeAllSubtasks && window.executeAllSubtasks()">🚀 Execute All Subtasks</button>';
                formattedHtml += '</div>';
                
                formattedHtml += '</div>';
                
                // Store subtasks for action handlers and set up the handlers
                if (window) {
                  window.pendingSubtasksData = subtasksArray;
                  
                  // Set up the add subtasks handler
                  window.addSubtasksToTask = () => {
                    if (window.pendingSubtasksData) {
                      // Emit event to parent to add subtasks
                      this.$emit('add-subtasks', {
                        taskId: this.localTaskId,
                        subtasks: window.pendingSubtasksData
                      });
                      
                      // Update button to show completion
                      const btns = document.querySelectorAll('.action-btn.primary');
                      btns.forEach(btn => {
                        if (btn.textContent.includes('Add All Subtasks')) {
                          btn.innerHTML = '✅ Subtasks Added';
                          btn.disabled = true;
                          btn.style.opacity = '0.6';
                          btn.style.cursor = 'not-allowed';
                        }
                      });
                      
                      this.$message.success('Subtasks added to task list!');
                    }
                  };
                  
                  // Set up the execute all subtasks handler
                  window.executeAllSubtasks = () => {
                    if (window.pendingSubtasksData) {
                      this.$emit('execute-all-subtasks', {
                        taskId: this.localTaskId,
                        subtasks: window.pendingSubtasksData
                      });
                      
                      this.$message.info('Starting execution of all subtasks...');
                    }
                  };
                }
                
                return formattedHtml;
              }
            } catch (parseError) {
              console.log('Error parsing JSON array:', parseError);
              // Fall through to try other formatting
            }
          }
          
          // Check if the response contains subtasks indicators with mixed content
          if (responseText.includes('Uncompleted Subtasks:') || 
              responseText.includes('subtasks:') || 
              responseText.includes('"subtasks"')) {
            
            // Try to find JSON array within the text
            const jsonMatch = responseText.match(/\[[\s\S]*?\]/);
            if (jsonMatch) {
              try {
                const subtasksArray = JSON.parse(jsonMatch[0]);
                
                if (Array.isArray(subtasksArray) && subtasksArray.length > 0) {
                  // Build formatted HTML for embedded subtasks
                  let formattedHtml = '<div class="embedded-subtasks">';
                  formattedHtml += '<h4>📋 Identified Subtasks:</h4>';
                  formattedHtml += '<ol class="subtasks-list">';
                  
                  subtasksArray.forEach((task, index) => {
                    const status = task.completed || task.status === 'completed' ? '✅' : '⏳';
                    const title = task.title || task.name || `Task ${index + 1}`;
                    const description = task.description ? `<div class="subtask-desc">${task.description}</div>` : '';
                    const time = task.estimated_time ? ` <span class="time-badge">${task.estimated_time}</span>` : '';
                    
                    formattedHtml += `<li class="subtask-item">
                      <div class="subtask-main">
                        <span class="status-icon">${status}</span>
                        <span class="subtask-title">${title}</span>${time}
                      </div>
                      ${description}
                    </li>`;
                  });
                  
                  formattedHtml += '</ol></div>';
                  
                  // Get text before and after the JSON array
                  const beforeJson = responseText.substring(0, responseText.indexOf(jsonMatch[0]));
                  const afterJson = responseText.substring(responseText.indexOf(jsonMatch[0]) + jsonMatch[0].length);
                  
                  // Return formatted version with subtasks displayed as list
                  return beforeJson + formattedHtml + afterJson;
                }
              } catch (parseError) {
                console.log('Error parsing embedded subtasks JSON:', parseError);
              }
            }
          }
        } catch (error) {
          console.log('Error formatting response:', error);
        }
        
        // Return original response if no special formatting needed
        return responseText;
      },
      
      formatMessageContent(message) {
        // For already displayed messages, just return the content as is
        // The formatting is now done in formatCompleteResponse
        return message.content || '';
      },
      
      convertDataObjectToJsonSchema(dataObject) {
        // Helper function to convert data object structure to JSON schema format
        console.log('Converting data object to JSON schema:', dataObject);
        
        if (!dataObject) {
          return {};
        }
        
        // Handle ARRAY type data objects
        if (dataObject.type === 'ARRAY') {
          const schema = {
            type: 'array',
            description: dataObject.description || `Array of ${dataObject.name}`,
            items: []
          };
          
          // Handle new multi-type array structure
          if (dataObject.arrayItems && dataObject.arrayItems.length > 0) {
            // If multiple item types, create a schema for each
            if (dataObject.arrayItems.length === 1) {
              const item = dataObject.arrayItems[0];
              if (item.type === 'object' && item.properties) {
                schema.items = this.convertPropertiesToSchema(item.properties);
              } else {
                schema.items = { type: this.mapDataTypeToJsonType(item.type) };
              }
            } else {
              // Multiple item types - use oneOf
              schema.items = {
                oneOf: dataObject.arrayItems.map(item => {
                  if (item.type === 'object' && item.properties) {
                    return this.convertPropertiesToSchema(item.properties);
                  } else {
                    return { type: this.mapDataTypeToJsonType(item.type) };
                  }
                })
              };
            }
          } 
          // Handle legacy itemType/itemProperties structure
          else if (dataObject.itemType) {
            if (dataObject.itemType === 'object' && dataObject.itemProperties) {
              schema.items = this.convertPropertiesToSchema(dataObject.itemProperties);
            } else {
              schema.items = { type: this.mapDataTypeToJsonType(dataObject.itemType) };
            }
          }
          
          return schema;
        }
        
        // Handle OBJECT type data objects
        return this.convertPropertiesToSchema(dataObject.properties || []);
      },
      
      convertPropertiesToSchema(properties) {
        const schema = {
          type: 'object',
          properties: {},
          required: []
        };
        
        if (!properties || properties.length === 0) {
          return schema;
        }
        
        properties.forEach(prop => {
          const propName = prop.name || 'unnamed';
          
          // Handle nested objects
          if (prop.type === 'object' && prop.children) {
            schema.properties[propName] = this.convertPropertiesToSchema(prop.children);
            if (prop.description) {
              schema.properties[propName].description = prop.description;
            }
          }
          // Handle arrays
          else if (prop.type === 'array') {
            schema.properties[propName] = {
              type: 'array',
              description: prop.description || ''
            };
            
            // Check if it's an array of objects
            if (prop.children && prop.children.length > 0) {
              schema.properties[propName].items = this.convertPropertiesToSchema(prop.children);
            } else if (prop.itemType) {
              // Legacy structure support
              if (prop.itemType === 'object' && prop.itemProperties) {
                schema.properties[propName].items = this.convertPropertiesToSchema(prop.itemProperties);
              } else {
                schema.properties[propName].items = { type: this.mapDataTypeToJsonType(prop.itemType) };
              }
            } else {
              // Default to string items if not specified
              schema.properties[propName].items = { type: 'string' };
            }
          }
          // Handle primitive types
          else {
            schema.properties[propName] = {
              type: this.mapDataTypeToJsonType(prop.type),
              description: prop.description || ''
            };
          }
          
          // Mark as required (you can customize this logic)
          if (prop.required !== false) {
            schema.required.push(propName);
          }
        });
        
        // Remove required array if empty
        if (schema.required.length === 0) {
          delete schema.required;
        }
        
        return schema;
      },
      
      mapDataTypeToJsonType(dataType) {
        // Map data object types to JSON schema types
        const typeMap = {
          'string': 'string',
          'number': 'number',
          'boolean': 'boolean',
          'object': 'object',
          'array': 'array',
          'integer': 'integer',
          'float': 'number',
          'double': 'number',
          'date': 'string', // JSON doesn't have a date type, use string with format
          'datetime': 'string',
          'time': 'string',
          'any': 'string' // Default to string for unknown types
        };
        
        return typeMap[dataType && dataType.toLowerCase()] || 'string';
      },
      
      buildDataStructure(dataObject) {
        // Build actual data structure from data object properties (similar to buildStructurePreview in DataObjectManagement)
        console.log('Building data structure from data object:', dataObject);
        
        if (!dataObject) {
          return {};
        }
        
        if (dataObject.type === 'ARRAY') {
          // Use new arrayItems structure if available
          if (dataObject.arrayItems && dataObject.arrayItems.length > 0) {
            // Create two example items for each defined array element type
            const exampleItems = [];
            dataObject.arrayItems.forEach(item => {
              if (item.type === 'object' && item.properties) {
                // Add two examples of the object structure
                exampleItems.push(this.buildPropertiesStructure(item.properties));
                exampleItems.push(this.buildPropertiesStructure(item.properties));
              } else {
                // Add two examples of the simple type with restrictions
                exampleItems.push(this.getTypeExample(item.type, item.name, item.description, item));
                exampleItems.push(this.getTypeExample(item.type, item.name, item.description, item));
              }
            });
            return exampleItems;
          }
          // Fallback to old structure for backward compatibility
          if (dataObject.itemType === 'object') {
            // Show two examples to illustrate multiple items
            return [
              this.buildPropertiesStructure(dataObject.itemProperties || []),
              this.buildPropertiesStructure(dataObject.itemProperties || [])
            ];
          }
          // Show two examples to illustrate multiple items
          return [
            this.getTypeExample(dataObject.itemType || 'string', null, null, dataObject),
            this.getTypeExample(dataObject.itemType || 'string', null, null, dataObject)
          ];
        }
        
        // Handle OBJECT type
        return this.buildPropertiesStructure(dataObject.properties || []);
      },
      
      buildPropertiesStructure(properties) {
        const structure = {};
        
        if (!properties || properties.length === 0) {
          return structure;
        }
        
        properties.forEach(prop => {
          const propName = prop.name || 'unnamed';
          
          if (prop.type === 'object') {
            structure[propName] = this.buildPropertiesStructure(prop.children || []);
          } else if (prop.type === 'array') {
            // Handle new arrayItems structure first
            if (prop.arrayItems && prop.arrayItems.length > 0) {
              // Create two example items for each defined array element type
              const exampleItems = [];
              prop.arrayItems.forEach(item => {
                if (item.type === 'object' && item.properties) {
                  // Add two examples of the object structure
                  exampleItems.push(this.buildPropertiesStructure(item.properties));
                  exampleItems.push(this.buildPropertiesStructure(item.properties));
                } else {
                  // Add two examples of the simple type with restrictions
                  exampleItems.push(this.getTypeExample(item.type, item.name, item.description, item));
                  exampleItems.push(this.getTypeExample(item.type, item.name, item.description, item));
                }
              });
              structure[propName] = exampleItems;
            }
            // Fallback to old itemType structure for backward compatibility
            else if (prop.itemType === 'object') {
              // Show two examples to illustrate multiple items
              structure[propName] = [
                this.buildPropertiesStructure(prop.children || []),
                this.buildPropertiesStructure(prop.children || [])
              ];
            } else {
              const itemTypeExample = this.getTypeExample(prop.itemType || 'string', prop.name, prop.description, prop);
              // Show two examples to illustrate multiple items
              structure[propName] = [itemTypeExample, itemTypeExample];
            }
          } else {
            structure[propName] = this.getTypeExample(prop.type, prop.name, prop.description, prop);
          }
        });
        
        return structure;
      },
      
      getTypeExample(type, name, description, property = null) {
        // Build the type string with restrictions
        let typeStr = `<${type}>`;
        
        // Add restriction info if available with clearer format
        if (property && property.restrictionType && property.restrictionType !== 'none') {
          if (property.restrictionType === 'list' && property.restrictionValues && property.restrictionValues.length > 0) {
            const validValues = property.restrictionValues.filter(v => v && v.trim()).join(', ');
            if (validValues) {
              typeStr = `<${type}> Only [${validValues}] allowed`;
            }
          } else if (property.restrictionType === 'range' && property.restrictionRange) {
            if (type === 'number' && (property.restrictionRange.min !== undefined || property.restrictionRange.max !== undefined)) {
              const min = property.restrictionRange.min !== undefined ? property.restrictionRange.min : '...';
              const max = property.restrictionRange.max !== undefined ? property.restrictionRange.max : '...';
              typeStr = `<${type}> Range: ${min} to ${max}`;
            } else if (type === 'string' && (property.restrictionRange.minLength !== undefined || property.restrictionRange.maxLength !== undefined)) {
              const minLen = property.restrictionRange.minLength !== undefined ? property.restrictionRange.minLength : '0';
              const maxLen = property.restrictionRange.maxLength !== undefined ? property.restrictionRange.maxLength : '...';
              typeStr = `<${type}> Length: ${minLen}-${maxLen} chars`;
            }
          } else if (property.restrictionType === 'regex' && property.restrictionPattern) {
            typeStr = `<${type}> Must match: ${property.restrictionPattern}`;
          }
        }
        
        // Add the name or default label
        if (name && name.trim()) {
          return `${typeStr} ${name}`;
        }
        
        // Return with default labels based on type
        const defaultLabels = {
          'string': 'text',
          'number': 'number',
          'integer': 'integer',
          'float': 'float',
          'double': 'double',
          'boolean': 'boolean',
          'date': 'date',
          'datetime': 'datetime',
          'time': 'time',
          'any': 'value'
        };
        
        return `${typeStr} ${defaultLabels[type && type.toLowerCase()] || 'value'}`;
      },
      
      // async createAiffectChains() {
      //   try {
      //     const user_id = "681836d094c784430b8b4580";
          
      //     // Create chains for all AI methods
          
         
          
      //     console.log('Successfully created Aiffect Chains for all AI processing tasks');
          
      //   } catch (error) {
      //     console.error('Error creating Aiffect Chains:', error);
      //   }
      // },

      async queryAIChain(taskOperation, skipConfirmation = false, userInstructions = '') {
        try {
          // Clear any previous error messages
          this.messages = this.messages.filter(msg => 
            !msg.content.includes('❌ Error occurred during AI Chain execution')
          );
          
          // Check if we have workflows in cart - use them instead of single chain
          if (this.workflowCart && this.workflowCart.length > 0) {
            if (!skipConfirmation) {
              // Show information about workflow cart execution
              this.messages.push({
                role: 'assistant',
                content: `<strong>Workflow Cart Ready: ${this.workflowCart.length} workflow(s) to execute</strong><br><br>
                  <strong>Context Items:</strong><br>
                  ${this.formatContextForDisplay()}<br><br>
                  <strong>Workflows in cart (will execute sequentially):</strong><br>
                  ${this.workflowCart.map((workflow, index) => {
                    const effectCount = workflow.effect_list ? workflow.effect_list.length : 0;
                    return `${index + 1}. ${workflow.name || 'Unnamed Workflow'} (${effectCount} step${effectCount !== 1 ? 's' : ''})`;
                  }).join('<br>')}<br><br>
                  <em>The output from each workflow will be used as input for the next workflow in the sequence.</em><br><br>
                  <em>Please provide any additional instructions or context you'd like to include before executing the workflow cart. Type your input below or just click Send to proceed with the current context.</em>`
              });
              
              // Scroll to show new messages
              this.$nextTick(() => {
                this.scrollToBottom();
              });

              // Set flag to indicate we're waiting for user confirmation
              this.waitingForChainConfirmation = true;
              this.pendingTaskOperation = taskOperation;
              return;
            } else {
              // Skip confirmation and execute directly
              await this.executeWorkflowCartSequentially(taskOperation, userInstructions);
              return;
            }
          } else {
            // Single chain execution (original logic)
            // Check if we have the chain data with effects
            if (!this.current_chain || !this.current_chain.effect_list) {
              this.messages.push({
                role: 'assistant',
                content: `❌ Chain configuration not found or invalid`
              });
              return;
            }

            if (!skipConfirmation) {
              // Show chain information and context to user
              this.messages.push({
                role: 'assistant',
                content: `<strong>AI Chain Ready: ${(taskOperation && taskOperation.operation_name) || (this.current_chain && this.current_chain.name) || 'AI Chain'}</strong><br><br>
                  <strong>Context Items:</strong><br>
                  ${this.formatContextForDisplay()}<br><br>
                  <strong>This chain will execute ${this.current_chain.effect_list.length} step${this.current_chain.effect_list.length > 1 ? 's' : ''}:</strong><br>
                  ${this.current_chain.effect_list.map((effect, index) => `${index + 1}. ${effect.name || effect.type || 'Processing'}`).join('<br>')}<br><br>
                  <em>Please provide any additional instructions or context you'd like to include before executing this chain. Type your input below or just click Send to proceed with the current context.</em>`
              });

              // Scroll to show new messages
              this.$nextTick(() => {
                this.scrollToBottom();
              });

              // Set flag to indicate we're waiting for user confirmation
              this.waitingForChainConfirmation = true;
              this.pendingTaskOperation = taskOperation;
              return;
            } else {
              // Skip confirmation and execute directly
              await this.executeChainWithUserInput(taskOperation, userInstructions);
              return;
            }
          }

        } catch (error) {
          // Add error message to dialogue
          this.messages.push({
            role: 'assistant',
            content: `❌ Error occurred during AI Chain execution: ${error.message || 'Unknown error'}`
          });
          
          this.$nextTick(() => {
            this.scrollToBottom();
          });
          
          console.error('Error with queryAIChain:', error);
          throw error;
        }
      },

      async executeEffect(effect, inputContent, taskOperation, userAdditionalInput = '', effectIndex = 0) {
        try {
          // Debug logging to understand what we're working with
          console.log('executeEffect called with:', {
            effect: effect,
            effectProp: effect.prop,
            effectPrompt: effect.prompt,
            effectContent: effect.content,
            effectName: effect.name,
            effectOutputForm: effect.output_form,
            hasOutputForm: !!(effect.output_form && effect.output_form.trim() !== ''),
            inputContentLength: inputContent?.length || 0,
            inputContentPreview: typeof inputContent === 'string' ? inputContent.substring(0, 100) + '...' : JSON.stringify(inputContent)?.substring(0, 100) + '...',
            userAdditionalInput: userAdditionalInput
          });

          // Prepare the prompt based on effect configuration - prioritize effect.prop (the actual AI instructions)
          let basePrompt = effect.prop || effect.prompt || effect.content || '';
          
          // Ensure we have the effect's AI instructions - this is critical
          if (!basePrompt || basePrompt.trim() === '') {
            console.error('CRITICAL: Effect has no prop/prompt/content defined!', effect);
            // If there's no AI instructions at all, we need to fail or create a meaningful one
            basePrompt = effect.prop || effect.prompt || `Please process the following content. Task: ${effect.name || effect.type || 'AI Processing'}`;
          }

          let prompt = basePrompt;
          
          // Convert inputContent to string if it's not already
          let inputContentStr = inputContent;
          if (typeof inputContent !== 'string') {
            inputContentStr = JSON.stringify(inputContent);
          }
          
          // Replace placeholders in prompt with actual context
          if (prompt.includes('{{input}}') || prompt.includes('${input}')) {
            prompt = prompt.replace(/\{\{input\}\}/g, inputContentStr || '').replace(/\$\{input\}/g, inputContentStr || '');
            console.log('Applied placeholder replacement in prompt');
          } else if (inputContentStr && inputContentStr.trim() !== '') {
            // If no placeholder, append input as context
            prompt = `${prompt}\n\nContext:\n${inputContentStr}`;
            console.log('Appended context to prompt (no placeholders found)');
          }

          // Append user's additional input if provided
          if (userAdditionalInput && userAdditionalInput.trim() !== '') {
            prompt = `${prompt}\n\nAdditional user instructions: ${userAdditionalInput}`;
            console.log('Added user additional input to prompt');
          }

          // Handle output_form if specified
          console.log('DEBUG: Full effect object:', effect);
          console.log('DEBUG: Checking output_form condition:', {
            output_form: effect.output_form,
            type: typeof effect.output_form,
            hasValue: !!effect.output_form,
            trimmedNotEmpty: effect.output_form && effect.output_form.trim() !== '',
            conditionResult: !!(effect.output_form && effect.output_form.trim() !== ''),
            effectIndex: effectIndex
          });
          
          // Also try direct string check for robustness
          const hasOutputForm = effect.output_form && 
                               typeof effect.output_form === 'string' && 
                               effect.output_form.length > 0 && 
                               effect.output_form !== '';
          
          console.log('DEBUG: Alternative condition check:', {
            hasOutputForm,
            directStringCheck: effect.output_form === '68baff0c94c784e2568b45e7'
          });
          
          // Use a more direct approach to avoid Vue reactivity issues
          const outputFormValue = String(effect.output_form || '');
          console.log('DEBUG: About to check condition with outputFormValue:', outputFormValue);
          
          if (outputFormValue && outputFormValue.trim() !== '') {
            console.log('*** OUTPUT_FORM LOGIC TRIGGERED ***', effect.output_form);
            console.warn('*** OUTPUT_FORM LOGIC TRIGGERED ***', effect.output_form); // Make it more visible
            
            // Fetch the data object structure directly by ID
            try {
              console.log('Fetching data object with ID:', effect.output_form);
              const dataObjectResponse = await postRequest('getDataObject', {
                id: effect.output_form
              });
              
              if (dataObjectResponse?.data?.code === 0 && dataObjectResponse?.data?.data) {
                const targetDataObject = dataObjectResponse.data.data;
                console.log('Successfully fetched data object for output_form:', targetDataObject);
                  
                  // Convert the data object properties to actual data structure
                  const dataStructure = this.buildDataStructure(targetDataObject);
                  const dataStructureString = JSON.stringify(dataStructure, null, 2);
                  
                  // Append the output format requirement to the prompt
                  prompt = `${prompt}\n\n===== MANDATORY JSON OUTPUT FORMAT =====\n\nYou MUST respond with ONLY a valid JSON object. Your ENTIRE response must be parseable JSON that conforms to this exact structure:\n\n${dataStructureString}\n\n**CRITICAL REQUIREMENTS:**\n\n1. OUTPUT FORMAT - ABSOLUTELY NO MARKDOWN:\n   - Start your response directly with the opening brace "{"\n   - End your response with the closing brace "}"\n   - NO text before or after the JSON\n   - NO markdown formatting whatsoever:\n     • DO NOT write \`\`\`json at the beginning\n     • DO NOT write \`\`\` at the end\n     • DO NOT wrap the JSON in any code blocks\n     • DO NOT use backticks anywhere\n   - NO explanations, comments, or commentary\n   - ONLY pure, raw, valid JSON text\n\n2. DATA STRUCTURE:\n   - All required fields MUST be present\n   - Follow the exact field names (case-sensitive)\n   - Use correct data types:\n     • string: text values in quotes\n     • number: numeric values without quotes\n     • boolean: true or false (lowercase, no quotes)\n     • array: values in square brackets []\n     • object: nested structures in curly braces {}\n   - For arrays: provide realistic data when possible\n   - For missing optional data: use null or omit the field\n\n3. CONTENT GUIDELINES:\n   - Extract and structure ALL relevant information from the context\n   - Ensure data accuracy and completeness\n   - Maintain consistency across related fields\n   - Use appropriate formatting for each data type\n\n4. VALIDATION:\n   - Your response MUST parse successfully with JSON.parse()\n   - No trailing commas\n   - Proper quote escaping for strings containing quotes\n   - Valid Unicode encoding for special characters\n\n⚠️ FINAL WARNING: Output ONLY the JSON object itself. The first character must be "{" and the last character must be "}". No \`\`\`json prefix, no \`\`\` suffix, no markdown formatting of any kind.`;
                  
                  console.log('Appended output format instructions to prompt');
              } else {
                console.warn('Data object not found or error in response:', {
                  output_form: effect.output_form,
                  response: dataObjectResponse?.data
                });
              }
            } catch (error) {
              console.error('Error fetching data object for output_form:', error);
              // Continue without output formatting if there's an error
            }
          } else {
            console.log('DEBUG: output_form condition not met or empty:', {
              output_form: effect.output_form,
              outputFormValue: outputFormValue,
              reason: !outputFormValue ? 'no output_form' : 'empty string'
            });
            console.warn('*** OUTPUT_FORM LOGIC NOT TRIGGERED ***', {
              originalValue: effect.output_form,
              stringValue: outputFormValue
            });
          }

          // Debug: Log the final prompt that will be sent to AI
          console.log('Final prompt to be sent to AI:', {
            promptLength: prompt.length,
            promptPreview: prompt.substring(0, 200) + '...',
            hasJsonFormatting: prompt.includes('MANDATORY JSON OUTPUT FORMAT'),
            fullPrompt: prompt // This might be long, but useful for debugging
          });

          // Safely extract operation_id - check multiple possible locations
          let operation_id = null;
          if (taskOperation) {
            // Log to understand the structure
            console.log('taskOperation in executeEffect:', taskOperation);
            
            if (taskOperation._id) {
              operation_id = (taskOperation._id.$oid || taskOperation._id.$id) || taskOperation._id.$oid || taskOperation._id;
            } else if (taskOperation.id) {
              operation_id = taskOperation.id;
            } else if (taskOperation.operation_id) {
              operation_id = taskOperation.operation_id;
            }
          }
          
          // If still no operation_id, try selectedTaskOperation
          if (!operation_id && this.selectedTaskOperation) {
            console.log('Using selectedTaskOperation:', this.selectedTaskOperation);
            if (this.selectedTaskOperation._id) {
              operation_id = (selectedTaskOperation._id.$oid || selectedTaskOperation._id.$id) || this.selectedTaskOperation._id.$oid || this.selectedTaskOperation._id;
            } else if (this.selectedTaskOperation.id) {
              operation_id = this.selectedTaskOperation.id;
            }
          }

          const effectModel = effect.model || 'gpt-4';

          // Special handling for Gemini image generation - add collection title to prompt
          if (effectModel === 'gemini-3-pro-image-preview') {
            try {
              // Extract collection title from context
              let collectionTitle = '';
              if (typeof inputContent === 'string') {
                const contextObj = JSON.parse(inputContent);
                collectionTitle = contextObj.title || contextObj.collection_title || '';
              } else if (inputContent && typeof inputContent === 'object') {
                collectionTitle = inputContent.title || inputContent.collection_title || '';
              }

              if (collectionTitle && collectionTitle.trim() !== '') {
                // Enhance prompt to include title text on the image
                prompt = `${prompt}\n\n**IMPORTANT: You MUST include the following text prominently on the image as the title:**\n"${collectionTitle}"\n\nPlace this title text in a visually appealing position on the cover image (e.g., centered at the top or middle). Use a clear, readable font that complements the image design.`;
                console.log('Enhanced Gemini prompt with collection title:', collectionTitle);
              }
            } catch (error) {
              console.warn('Could not extract collection title for Gemini prompt:', error);
            }
          }

          // Check if this is a Midjourney effect
          if (effectModel === 'midjourney') {
            // Add a progress message to chat history
            this.messages.push({
              role: "assistant",
              content: `Current progress in image generation: ${this.progress}`,
              type: "createCollectionCoverPrompt",
              imgUrl: "",
              taskId: "",
              action: ""
            });
            this.scrollToBottom();
            
            // Execute Midjourney model
            const result = await this.exeMidJModel(prompt);
            if (result) {
              // Update the last message with image data
              const lastMessage = this.messages[this.messages.length - 1];
              this.$set(lastMessage, "imgUrl", result.url);
              this.$set(lastMessage, "taskId", result.taskId);
              this.$set(lastMessage, "action", result.action);
              
              // Return the image URL as the result
              return result.url;
            } else {
              throw new Error('Failed to generate image with Midjourney');
            }
          } else {
            // Call the backend's exeModelGeneral method with required parameters
            // Split prop and context for backend processing
            const requestParams = {
              content: '', // Leave empty as backend will combine prop and context
              prop: prompt, // The AI instructions/prompt from the effect (including any output format requirements)
              context: inputContentStr || '', // The context materials passed to this effect (converted to string)
              model: effectModel
            };

            // Add tracking parameters - always include these for chain execution
            requestParams.operation_id = operation_id;
            requestParams.task_id = taskOperation?.task_id || this.localTaskId;
            requestParams.user_id = getUserId();
            
            // Ensure chain_id is always present - critical for backend processing
            const chainId = taskOperation?.chain_id || this.current_chain_id;
            if (!chainId) {
              console.error('CRITICAL: No chain_id available for exeModelGeneral request!', {
                taskOperation,
                current_chain_id: this.current_chain_id
              });
              throw new Error('Chain ID is required for chain execution');
            }
            requestParams.chain_id = chainId;
            requestParams.effect_index = effectIndex; // Index of this effect in the chain's effect_list
            requestParams.task_name = taskOperation?.operation_name || this.current_chain?.name || 'AI Chain Execution';
            requestParams.source_label = 'Task Operation';
            
            // Add chain's system_prompt if available
            const chainSystemPrompt = this.current_chain?.system_prompt;
            if (chainSystemPrompt && chainSystemPrompt.trim() !== '') {
              requestParams.system_prompt = chainSystemPrompt;
            }
            
            // Add user input if provided
            if (userAdditionalInput && userAdditionalInput.trim() !== '') {
              requestParams.user_input = userAdditionalInput;
            }
            
            console.log('exeModelGeneral request params:', requestParams);

            const response = await postRequest('exeModelGeneral', requestParams);

            if (response && response.data) {
              if (response.data.code === 0 && response.data.data) {
                let aiResponse = response.data.data;

                // Handle Gemini image model response (returns Base64 image in text format)
                if (effectModel === 'gemini-3-pro-image-preview' && response.data.raw_response) {
                  try {
                    console.log('Processing Gemini image model response');

                    // Parse the raw_response JSON (it might already be an object)
                    const rawResponseObj = typeof response.data.raw_response === 'string'
                      ? JSON.parse(response.data.raw_response)
                      : response.data.raw_response;

                    console.log('Parsed rawResponseObj:', rawResponseObj);

                    // Navigate through the structure to get the Base64 data
                    // Structure: rawResponseObj.data.candidates[0].content.parts[...] (find the one with inlineData)
                    const candidates = rawResponseObj.data?.candidates;
                    if (candidates && candidates.length > 0) {
                      const parts = candidates[0]?.content?.parts;
                      if (parts && parts.length > 0) {
                        // Loop through parts to find the one with inlineData (image data)
                        for (const part of parts) {
                          const inlineData = part?.inlineData;
                          if (inlineData && inlineData.data) {
                            const base64String = inlineData.data;
                            const mimeType = inlineData.mimeType || 'image/png';

                            // Convert Base64 to data URL
                            const imageDataUrl = `data:${mimeType};base64,${base64String}`;

                            console.log('Gemini image generated successfully, Base64 length:', base64String.length);

                            // Add the image to messages with regeneration info
                            this.messages.push({
                              role: 'assistant',
                              content: '',
                              type: 'createCollectionCoverPrompt',
                              imgUrl: imageDataUrl,
                              taskId: '',
                              action: '',
                              // Store info needed for regeneration
                              geminiRegenInfo: {
                                effect: effect,
                                inputContent: inputContent,
                                taskOperation: taskOperation,
                                userAdditionalInput: userAdditionalInput,
                                effectIndex: effectIndex
                              },
                              regenerating: false
                            });

                            this.scrollToBottom();

                            // Return empty string so the text response (Base64 string) is NOT displayed in messages
                            return '';
                          }
                        }
                      }
                    }

                    console.error('Could not find Base64 image data in Gemini response');
                    console.error('rawResponseObj:', rawResponseObj);
                    throw new Error('Invalid Gemini image response structure');
                  } catch (error) {
                    console.error('Error parsing Gemini image response:', error);
                    throw error;
                  }
                }

                // Clean JSON if output_form is specified
                const hasOutputForm = effect.output_form && 
                                     typeof effect.output_form === 'string' && 
                                     effect.output_form.trim() !== '';
                
                if (hasOutputForm && typeof aiResponse === 'string') {
                  console.log('Cleaning JSON response for output_form:', effect.output_form);
                  
                  // Remove markdown code blocks if present
                  // Remove ```json from the beginning and ``` from the end
                  aiResponse = aiResponse
                    .replace(/^```json\s*/i, '')  // Remove ```json from start (case insensitive)
                    .replace(/^```\s*/i, '')       // Remove ``` from start if no json label
                    .replace(/\s*```$/i, '')       // Remove ``` from end
                    .trim();                       // Remove any extra whitespace
                  
                  // Additional cleaning for any remaining markdown artifacts
                  // Check if the response starts with { and ends with }
                  const firstChar = aiResponse.charAt(0);
                  const lastChar = aiResponse.charAt(aiResponse.length - 1);
                  
                  // If it doesn't look like valid JSON, try to extract JSON from the content
                  if (firstChar !== '{' && firstChar !== '[') {
                    // Try to find JSON object or array in the response
                    const jsonMatch = aiResponse.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
                    if (jsonMatch) {
                      aiResponse = jsonMatch[1];
                      console.log('Extracted JSON from response');
                    }
                  }
                  
                  console.log('Cleaned JSON response:', aiResponse.substring(0, 100) + '...');
                }
                
                // Return the AI response content (cleaned if necessary)
                return aiResponse;
              } else {
                throw new Error(response.data.msg || 'Failed to execute model');
              }
            } else {
              throw new Error('Invalid response from backend');
            }
          }

        } catch (error) {
          console.error('Error executing effect:', error);
          throw error;
        }
      },
      handleEnter(e) {
        if (e.shiftKey) return;
        e.preventDefault();
        this.sendMessage(); 
      },

      async sendMessage() {
        // Check if we can send based on the new logic
        if (!this.canSendMessage) return;
        
        // If no user input but we have context and workflows, treat as empty input
        const hasUserInput = this.userInput.trim().length > 0;

        // Check if we're waiting for chain confirmation
        if (this.waitingForChainConfirmation) {
          const userAdditionalInput = this.userInput.trim();
          
          // Add user's confirmation/additional input message
          this.messages.push({
            role: "user",
            content: userAdditionalInput || "Proceed with execution"
          });
          
          // Clear the input and reset flag
          this.userInput = "";
          this.waitingForChainConfirmation = false;
          
          // Check if we have workflows in cart to execute sequentially
          if (this.workflowCart && this.workflowCart.length > 0) {
            await this.executeWorkflowCartSequentially(this.pendingTaskOperation, userAdditionalInput);
          } else {
            // Execute single chain with user's additional input
            await this.executeChainWithUserInput(this.pendingTaskOperation, userAdditionalInput);
          }
          this.pendingTaskOperation = null;
          return;
        }

        // Check if we have both context_pot and workflow cart loaded
        // If so, treat user input as instructions to trigger the workflow operation
        if (this.globalContextPot && this.globalContextPot.length > 0 && this.workflowCart && this.workflowCart.length > 0) {
          const userInstructions = this.userInput.trim();
          
          // Add user's instruction message
          this.messages.push({
            role: "user",
            content: userInstructions
          });
          
          // Clear the input
          this.userInput = "";
          
          // Execute the workflow cart with context and user instructions
          await this.executeAllWorkflowsInCart(userInstructions);
          return;
        }

        const contextString = this.getContextString();
        // Handle both array and string context for relevance check
        const contextForRelevance = Array.isArray(contextString) ? JSON.stringify(contextString) : contextString;
        const relevance_check = `Check the relevance of following user's input/request to the collection context (${contextForRelevance}). Allow language switches. Only answer/reply relevant questions. If not relevant, tell the user politely. If user's question is relevant, answer it based on what you have been trained and your whole knowledge base. Do not limit the answers only to the context content. If relevant, do NOT mention / affirm or admit its relevance in the answer (just answer directly). Do not provide any texts of judgement on whether the context content provide answers to or touch this question. Still, only answer relevant questions.`;
        const userMessage = relevance_check + this.userInput.trim();
        
        var inputMessages = [...this.messages]; // Create a copy of the array

        inputMessages.push({
          role: "user",
          content: userMessage,
        })
        
        this.messages.push({
          role: "user",
          content: this.userInput.trim(),
        });
        this.userInput = "";
        this.isLoading = true;

        try {
          // Add placeholder for assistant message
          this.messages.push({
            role: "assistant",
            content: "Thinking...",
          });

          const that = this; // Store reference to component instance
          let responseBuffer = ""; // Buffer to accumulate the complete response

          await fetchEventSource(
            "https://web-backend-sg.reachable-edu.com/AIChat/Test.php",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                model: that.currentMode,
                messages: inputMessages,
              }),
              onopen(response) {
                console.log("Connection established", response);
              },
              onmessage: (event) => {
                if (that.currentMode.indexOf("claude") != -1 && event.data) {
                  const result = JSON.parse(event.data);
                  if (result.type === "message_delta") {
                    that.isGenerate = false;
                    that.isLoading = false;
                    return;
                  }
                } else if (event.data === "Connection closed") {
                  that.isGenerate = false;
                  that.isLoading = false;
                  return;
                }

                const result = JSON.parse(event.data);
                // Accumulate response in buffer instead of displaying directly
                if (that.currentMode.indexOf("claude") != -1) {
                  if (result.type === "content_block_delta") {
                    responseBuffer += result.delta.text;
                  }
                } else if (result.time && result.content) {
                  responseBuffer += result.content;
                }
              },
              onclose: () => {
                that.isGenerate = false;
                
                // Now process the complete response
                if (responseBuffer) {
                  // Replace the placeholder message with the formatted complete response
                  that.messages[that.messages.length - 1].content = that.formatCompleteResponse(responseBuffer);
                  
                  that.$nextTick(() => {
                    that.scrollToBottom();
                  });
                }
                
                // Clear context pot after successful message
                that.clearContextPot();
              },
              onerror: () => {
                this.isLoading = false;
                this.$message.error("Failed to send message");
              },
            }
          );
        } catch (error) {
          this.$message.error("Failed to send message" + error);
          this.isLoading = false;
        }
      },

      async processAIDelegatedTask(context) {
        try {
          this.isLoading = true;
          
          // Create task operation for AI processing
          const operationResponse = await postRequest('updateTaskOperation', {
            task_id: context.task_id,
            operation_type: 'ai_processing',
            operation_name: 'AI Task Analysis and Execution',
            user_id: context.operation_data.user_id,
            input_string: JSON.stringify({
              task_title: context.title,
              task_description: context.description,
              subtasks: context.subtasks,
              ai_prompt: context.ai_prompt
            }),
            status: 'pending'
          });
          
          if (operationResponse && operationResponse.data && operationResponse.data.code === 0) {
            this.selectedTaskOperation = operationResponse.data.data;
            
            // Don't add placeholder yet - will be handled differently for subtasks
            
            // Execute AI analysis
            await this.executeAIAnalysis(context);
          }
        } catch (error) {
          console.error('Error processing AI delegated task:', error);
          this.$message.error('Failed to process task with AI');
          this.isLoading = false;
        }
      },
      
      async executeAIAnalysis(context) {
        try {
          const that = this;
          
          // Check if this is a subtask generation request
          const isSubtaskGeneration = context.ai_prompt && context.ai_prompt.includes('break down') && context.ai_prompt.includes('actionable steps');
          
          // Initialize response buffer for subtasks
          let responseBuffer = '';
          
          // Prepare messages for AI - with subtask generation prompt if needed
          const systemPrompt = isSubtaskGeneration 
            ? `You are an AI assistant helping to analyze tasks and break them down into executable subtasks. 
               You MUST respond with a JSON array of subtasks in the following format:
               [
                 {
                   "title": "Subtask title",
                   "description": "Detailed description of what needs to be done",
                   "priority": 1,
                   "estimated_time": "30 minutes",
                   "dependencies": [],
                   "status": "pending"
                 }
               ]
               
               Important requirements:
               1. Return ONLY valid JSON array, no markdown formatting
               2. Each subtask should be specific and actionable
               3. Order subtasks by priority (1 = highest)
               4. Include realistic time estimates
               5. Mark dependencies if one task requires another to complete first
               6. Start response directly with [ and end with ]`
            : "You are an AI assistant helping to analyze and execute tasks. Provide detailed, actionable guidance.";
          
          const aiMessages = [
            {
              role: "system",
              content: systemPrompt
            },
            {
              role: "user",
              content: context.ai_prompt
            }
          ];
          
          await fetchEventSource(
            "https://web-backend-sg.reachable-edu.com/AIChat/Test.php",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                model: that.currentMode,
                messages: aiMessages,
              }),
              onopen(response) {
                console.log("AI Analysis connection established", response);
              },
              onmessage: (event) => {
                if (that.currentMode.indexOf("claude") != -1 && event.data) {
                  const result = JSON.parse(event.data);
                  if (result.type === "message_delta") {
                    that.isLoading = false;
                    return;
                  }
                } else if (event.data === "Connection closed") {
                  that.isLoading = false;
                  return;
                }

                const result = JSON.parse(event.data);
                
                // For subtask generation, accumulate response in buffer
                if (isSubtaskGeneration) {
                  if (that.currentMode.indexOf("claude") != -1) {
                    if (result.type === "content_block_delta") {
                      responseBuffer += result.delta.text;
                    }
                  } else if (result.time && result.content) {
                    responseBuffer += result.content;
                  }
                } else {
                  // For normal responses, also accumulate in buffer
                  if (that.currentMode.indexOf("claude") != -1) {
                    if (result.type === "content_block_delta") {
                      responseBuffer += result.delta.text;
                    }
                  } else if (result.time && result.content) {
                    responseBuffer += result.content;
                  }
                }
              },
              onclose: async () => {
                that.isLoading = false;
                
                // Always use the buffered response
                const aiResponse = responseBuffer;
                
                // Check if response is JSON subtasks
                let subtasks = null;
                let formattedMessage = '';
                
                if (isSubtaskGeneration) {
                  try {
                    // Clean the response - remove markdown if present
                    let cleanedResponse = aiResponse
                      .replace(/^```json\s*/i, '')
                      .replace(/^```\s*/i, '')
                      .replace(/\s*```$/i, '')
                      .trim();
                    
                    subtasks = JSON.parse(cleanedResponse);
                    
                    if (Array.isArray(subtasks)) {
                      // Successfully parsed subtasks
                      console.log('Parsed subtasks:', subtasks);
                      
                      // Format subtasks for display
                      formattedMessage = '<div class="subtasks-result">';
                      formattedMessage += '<h3>📋 Task Breakdown Complete</h3>';
                      formattedMessage += `<p>Successfully identified ${subtasks.length} subtasks:</p>`;
                      formattedMessage += '<div class="subtasks-list">';
                      
                      subtasks.forEach((task, index) => {
                        formattedMessage += `
                          <div class="subtask-item">
                            <div class="subtask-header">
                              <span class="subtask-number">${index + 1}.</span>
                              <span class="subtask-title">${task.title}</span>
                              <span class="subtask-time">⏱ ${task.estimated_time || 'N/A'}</span>
                            </div>
                            <div class="subtask-description">${task.description}</div>
                            ${task.dependencies && task.dependencies.length > 0 ? 
                              `<div class="subtask-deps">Dependencies: ${task.dependencies.join(', ')}</div>` : ''}
                          </div>`;
                      });
                      
                      formattedMessage += '</div>';
                      formattedMessage += '<div class="subtask-actions">';
                      formattedMessage += '<button class="add-subtasks-btn" onclick="window.addSubtasksToTask && window.addSubtasksToTask()">➕ Add Subtasks to Task List</button>';
                      formattedMessage += '</div>';
                      formattedMessage += '</div>';
                      
                      // Add formatted message to display
                      that.messages.push({
                        role: "assistant",
                        content: formattedMessage
                      });
                      
                      // Store subtasks for later use
                      that.pendingSubtasks = {
                        taskId: context.task_id,
                        subtasks: subtasks
                      };
                      
                      // Make the add function available globally
                      window.addSubtasksToTask = async () => {
                        if (that.pendingSubtasks) {
                          await that.createSubtasksForTask(that.pendingSubtasks.taskId, that.pendingSubtasks.subtasks);
                          that.$message.success('Subtasks added to task list!');
                          // Update button to show completion
                          const btn = document.querySelector('.add-subtasks-btn');
                          if (btn) {
                            btn.innerHTML = '✅ Subtasks Added';
                            btn.disabled = true;
                          }
                        }
                      };
                      
                      // Start automatic execution if enabled
                      if (context.auto_execute) {
                        await that.executeSubtasksSequentially(context.task_id, subtasks);
                      }
                    }
                  } catch (parseError) {
                    console.log('Response is not JSON subtasks, treating as regular analysis');
                    // Add as regular message if not valid JSON
                    that.messages.push({
                      role: "assistant",
                      content: aiResponse
                    });
                  }
                } else {
                  // For non-subtask responses, add the formatted complete message
                  that.messages.push({
                    role: "assistant",
                    content: that.formatCompleteResponse(aiResponse)
                  });
                }
                
                // Scroll to bottom after adding message
                that.$nextTick(() => {
                  that.scrollToBottom();
                });
                
                // Update task operation status
                if (that.selectedTaskOperation) {
                  await postRequest('updateTaskOperation', {
                    operation_id: (that.selectedTaskOperation._id.$oid || that.selectedTaskOperation._id.$id) || that.selectedTaskOperation.id,
                    task_id: context.task_id,
                    status: 'completed',
                    output_string: aiResponse,
                    subtasks: subtasks
                  });
                }
                
                // Emit task update event with subtasks if available
                that.$emit('task-updated', {
                  id: context.task_id,
                  ai_analysis: aiResponse,
                  subtasks: subtasks
                });
                
                that.$message.success(subtasks ? 'Task breakdown completed' : 'Task analysis completed');
              },
              onerror: () => {
                that.isLoading = false;
                that.$message.error("Failed to analyze task with AI");
              },
            }
          );
        } catch (error) {
          console.error('Error executing AI analysis:', error);
          this.$message.error('Failed to execute AI analysis');
          this.isLoading = false;
        }
      },

      async createSubtasksForTask(taskId, subtasks) {
        try {
          // Update task with subtasks
          const updateResponse = await postRequest('updateTask', {
            id: taskId,
            user_id: getUserId(),
            subtasks: subtasks
          });
          
          if (updateResponse?.data?.code === 0) {
            console.log('Subtasks created successfully');
            
            // Show subtasks in UI message
            let subtaskMessage = '✅ Created ' + subtasks.length + ' subtasks:\n\n';
            subtasks.forEach((subtask, index) => {
              subtaskMessage += `${index + 1}. ${subtask.title}\n`;
              if (subtask.description) {
                subtaskMessage += `   📝 ${subtask.description}\n`;
              }
              if (subtask.estimated_time) {
                subtaskMessage += `   ⏱️ Estimated: ${subtask.estimated_time}\n`;
              }
              subtaskMessage += '\n';
            });
            
            this.messages.push({
              role: 'system',
              content: subtaskMessage
            });
            
            this.$nextTick(() => {
              this.scrollToBottom();
            });
            
            return true;
          }
        } catch (error) {
          console.error('Error creating subtasks:', error);
          this.$message.error('Failed to create subtasks');
          return false;
        }
      },
      
      async executeSubtasksSequentially(taskId, subtasks) {
        try {
          this.messages.push({
            role: 'system',
            content: '🤖 Starting automatic execution of subtasks...'
          });
          
          for (let i = 0; i < subtasks.length; i++) {
            const subtask = subtasks[i];
            
            // Show progress
            this.messages.push({
              role: 'system',
              content: `\n▶️ Executing subtask ${i + 1}/${subtasks.length}: ${subtask.title}`
            });
            
            this.$nextTick(() => {
              this.scrollToBottom();
            });
            
            // Create task operation for this subtask
            const operationResponse = await postRequest('updateTaskOperation', {
              task_id: taskId,
              operation_type: 'ai_subtask_execution',
              operation_name: `Execute: ${subtask.title}`,
              user_id: getUserId(),
              input_string: JSON.stringify(subtask),
              status: 'pending'
            });
            
            if (operationResponse?.data?.code === 0) {
              const operation = operationResponse.data.data;
              
              // Execute AI for this subtask
              await this.executeSubtaskWithAI(subtask, operation, taskId);
              
              // Update subtask status
              subtasks[i].status = 'completed';
              await postRequest('updateTask', {
                id: taskId,
                user_id: getUserId(),
                subtasks: subtasks
              });
            }
            
            // Small delay between subtasks
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          this.messages.push({
            role: 'system',
            content: '✅ All subtasks have been executed successfully!'
          });
          
          this.$nextTick(() => {
            this.scrollToBottom();
          });
          
        } catch (error) {
          console.error('Error executing subtasks:', error);
          this.$message.error('Failed to execute subtasks automatically');
        }
      },
      
      async executeSubtaskWithAI(subtask, operation, taskId) {
        try {
          const that = this;
          
          // Prepare AI prompt for subtask execution
          const prompt = `Execute the following subtask:
            Title: ${subtask.title}
            Description: ${subtask.description}
            
            Provide specific implementation details, code snippets, or actionable steps to complete this subtask.`;
          
          const aiMessages = [
            {
              role: "system",
              content: "You are an AI assistant executing specific subtasks. Provide concrete, actionable implementation."
            },
            {
              role: "user",
              content: prompt
            }
          ];
          
          // Add placeholder for AI response
          this.messages.push({
            role: "assistant",
            content: ""
          });
          
          return new Promise((resolve, reject) => {
            fetchEventSource(
              "https://web-backend-sg.reachable-edu.com/AIChat/Test.php",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  model: that.currentMode,
                  messages: aiMessages,
                }),
                onopen(response) {
                  console.log("Subtask execution connection established", response);
                },
                onmessage: (event) => {
                  if (that.currentMode.indexOf("claude") != -1 && event.data) {
                    const result = JSON.parse(event.data);
                    if (result.type === "message_delta") {
                      return;
                    }
                  } else if (event.data === "Connection closed") {
                    return;
                  }

                  const result = JSON.parse(event.data);
                  if (that.currentMode.indexOf("claude") != -1) {
                    if (result.type === "content_block_delta") {
                      that.messages[that.messages.length - 1].content += result.delta.text;
                    }
                  } else if (result.time && result.content) {
                    that.messages[that.messages.length - 1].content += result.content;
                  }

                  that.$nextTick(() => {
                    that.scrollToBottom();
                  });
                },
                onclose: async () => {
                  // Update operation status
                  if (operation) {
                    await postRequest('updateTaskOperation', {
                      operation_id: operation._id?.$oid || operation._id?.$id || operation.id,
                      task_id: taskId,
                      status: 'completed',
                      output_string: that.messages[that.messages.length - 1].content
                    });
                  }
                  resolve();
                },
                onerror: (error) => {
                  console.error('Error executing subtask:', error);
                  reject(error);
                },
              }
            );
          });
        } catch (error) {
          console.error('Error in executeSubtaskWithAI:', error);
          throw error;
        }
      },
      
      scrollToBottom() {
        const container = this.$refs.messagesContainer;
        container.scrollTop = container.scrollHeight;
      },

      handleTaskSelected(task) {
        this.selectedTask = task;
      },

      handleBackToTaskList() {
        this.selectedTask = null;
      },
      
      handleTaskUpdated(updatedTask) {
        // Update the task in the tasks array
        const taskIndex = this.tasks.findIndex(t =>
          (t._id?.$oid || t._id?.$id || t.id) === (updatedTask._id?.$oid || updatedTask._id?.$id || updatedTask.id)
        );

        if (taskIndex !== -1) {
          // Update existing task instead of adding a new one
          this.$set(this.tasks, taskIndex, updatedTask);
          // Also update selectedTask if it's the same task
          if (this.selectedTask &&
              (this.selectedTask._id?.$oid || this.selectedTask._id?.$id || this.selectedTask.id) === (updatedTask._id?.$oid || updatedTask._id?.$id || updatedTask.id)) {
            this.selectedTask = updatedTask;
          }
        }
      },
      
      getDialogueTitle() {
        if (this.selectedTask) {
          return this.selectedTask.title;
        }
        // If we have a current chain, use its name
        if (this.current_chain && this.current_chain.name) {
          return this.current_chain.name;
        }
        // Fall back to task type specific titles only if appropriate
        if (this.currentTaskType === 'Collection Cover Generation') {
          return 'Collection Cover Generation';
        }
        // Default to generic AI Assistant
        return 'AI Assistant';
      },
      
      // async executeCoverGenerationChain() {
      //   try {
      //     this.isLoading = true;
      //     const context = JSON.parse(this.context_string);
          
      //     if (!this.coverGenerationChain) {
      //       await this.createCollectionCoverGenerationChain(context.user_id, context.collection_name, context.collection_description);
      //     }
          
      //                // Execute the chain with collection context
      //      const response = await this.updateChain('exeChain', {
      //        chain_id: (coverGenerationChain._id.$oid || coverGenerationChain._id.$id),
      //        id: '',
      //        name: `Cover Generation - ${context.collection_name}`,
      //        user_id: context.user_id,
      //        exe_result: [
      //          `Collection Title: ${context.collection_name}`,
      //          `Collection Description: ${context.collection_description}`,
      //          `Generate a professional cover image that captures the essence of this collection`
      //        ]
      //      });
          
      //     if (response && response.data && response.data.code === 0) {
      //       this.monitorChainExecution(response.data.data);
      //     } else {
      //       console.error('Failed to execute chain:', response);
      //       this.$message.error('Failed to execute cover generation chain');
      //     }
          
      //   } catch (error) {
      //     console.error('Error executing cover generation chain:', error);
      //     this.$message.error('Failed to start cover generation');
      //   } finally {
      //     this.isLoading = false;
      //   }
      // },

      
       
       getContextString() {
        // If context_pot has items, use it directly as array; otherwise fall back to context_string
        if (this.globalContextPot && this.globalContextPot.length > 0) {
          return this.globalContextPot; // Return array directly, backend will JSON encode it
        }
        return this.context_string;
      },

      clearContextPot() {
        this.$store.dispatch('contextPot/clearAll').then(result => {
          if (result.success) {
            this.$message.success(result.message);
          }
        });
      },

      removeFromPot(index) {
        this.$store.dispatch('contextPot/removeItem', index).then(result => {
          if (result.success) {
            this.$message.info(result.message);
          }
        });
      },

      getItemLabel(item) {
        if (typeof item === 'string') {
          try {
            const parsed = JSON.parse(item);
            // Handle different item types
            if (parsed.type === 'coder_todo') {
              return '📝 Coder Todo';
            } else if (parsed.type === 'task') {
              return '📋 Task';
            }
            return parsed.type || parsed.task || 'Context Item';
          } catch (e) {
            return 'Context Item';
          }
        }
        
        // Handle different item types for objects
        if (item.type === 'coder_todo') {
          return '📝 Coder Todo';
        } else if (item.type === 'task' || item.workflowSteps) {
          return '📋 Task';
        }
        
        return item.type || item.task || item.collection_name || item.title || 'Context Item';
      },

      getItemPreview(item) {
        if (typeof item === 'string') {
          try {
            const parsed = JSON.parse(item);
            
            // Handle coder todos
            if (parsed.type === 'coder_todo') {
              const name = parsed.name || parsed.title || 'Untitled Todo';
              const description = parsed.ai_description || parsed.description || '';
              const project = parsed.project ? ` [${parsed.project}]` : '';
              return `${name}${project}${description ? ': ' + description.substring(0, 50) : ''}`;
            }
            
            // Handle tasks
            if (parsed.type === 'task' || parsed.workflowSteps) {
              const title = parsed.title || 'Untitled Task';
              const description = parsed.description || '';
              const status = parsed.status ? ` (${parsed.status})` : '';
              return `${title}${status}${description ? ': ' + description.substring(0, 50) : ''}`;
            }
            
            return parsed.collection_name || parsed.collection_description || parsed.content || item.substring(0, 50) + '...';
          } catch (e) {
            return item.length > 50 ? item.substring(0, 50) + '...' : item;
          }
        }
        
        // Handle coder todos (object format)
        if (item.type === 'coder_todo') {
          const name = item.name || item.title || 'Untitled Todo';
          const description = item.ai_description || item.description || '';
          const project = item.project ? ` [${item.project}]` : '';
          const preview = `${name}${project}${description ? ': ' + description.substring(0, 50) : ''}`;
          return description.length > 50 ? preview + '...' : preview;
        }
        
        // Handle tasks (object format)
        if (item.type === 'task' || item.workflowSteps) {
          const title = item.title || 'Untitled Task';
          const description = item.description || '';
          const status = item.status ? ` (${item.status})` : '';
          const preview = `${title}${status}${description ? ': ' + description.substring(0, 50) : ''}`;
          return description.length > 50 ? preview + '...' : preview;
        }
        
        return item.collection_name || item.collection_description || item.content || JSON.stringify(item).substring(0, 50) + '...';
      },
      
      
      async monitorChainExecution(/* executionData */) {
        try {
          // Add a message to show execution started
          this.messages.push({
            role: 'assistant',
            content: 'AI Chain execution started. Processing your request...'
          });
          
          // Since we don't have a real-time API, we'll simulate the process
          // In production, you would poll the getChain API to check for new records
          this.isLoading = true;
          
          // Simulate processing time (in real implementation, you'd poll the API)
          setTimeout(async () => {
            try {
              // For now, we'll simulate a successful execution
              // In production, you would check the chain's records for the execution result
              
              if (this.currentTaskType === 'Collection Cover Generation') {
                // Simulate a generated cover URL
                // In production, this would come from the chain execution results
                const mockCoverUrl = `https://via.placeholder.com/300x424/4A90E2/FFFFFF?text=AI+Generated+Cover`;
                
                this.messages.push({
                  role: 'assistant',
                  content: `Cover generated successfully! <br><img src="${mockCoverUrl}" style="max-width: 300px; margin-top: 10px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">`
                });
                
                // Emit the cover URL to parent component
                this.$emit('cover-generated', mockCoverUrl);
                
                // Add action buttons
                this.messages.push({
                  role: 'assistant',
                  content: 'The cover has been applied to your collection. You can generate a new one by clicking the button above, or close this dialog to continue editing.'
                });
              } else {
                // Handle other task types
                this.messages.push({
                  role: 'assistant',
                  content: 'Task completed successfully!'
                });
              }
              
            } catch (error) {
              console.error('Error processing chain results:', error);
              this.messages.push({
                role: 'assistant',
                content: 'An error occurred while processing the results. Please try again.'
              });
            } finally {
              this.isLoading = false;
            }
          }, 5000); // Simulate 5 seconds of processing
          
        } catch (error) {
          console.error('Error monitoring chain execution:', error);
          this.messages.push({
            role: 'assistant',
            content: 'Failed to monitor chain execution. Please try again.'
          });
          this.isLoading = false;
        }
      },

      async takeAIResult(result, type = null) {
        console.log('takeAIResult called with:', { result, type });
        
        // Handle createPrivatePost directly within this component
        if (type === 'createPrivatePost') {
          await this.createPrivatePostFromAI(result);
        } else {
          // For other types, emit to parent
          this.$emit("use", result, type);
        }
      },
      
      async createPrivatePostFromAI(postData) {
        console.log('Creating private post from AI result:', postData);
        try {
          // Create a new post based on the AI-generated data
          const newPost = {
            id: '', // Empty for new post
            user_id: this.$store.state.user_id,
            title: postData.title || 'AI Generated Content',
            description: postData.description || 'Content generated by AI',
            content_type: postData.content_type || 'multiple_choice',
            answers: [],
            price: 0,
            labels: [],
            status: 'PRIVATE', // Set as private
            usd_price: 0,
            token_price: 0,
            collection_id: '',
            group_index: '',
            post_index: '',
            access_type: 'public'
          };

          // Process the answers/content from the AI result
          if (postData.answers && Array.isArray(postData.answers)) {
            newPost.answers = postData.answers.map(answer => ({
              content_type: answer.content_type || 'text',
              content: answer.content || '',
              fileList: answer.fileList || [],
              has_titles: answer.has_titles || false,
              is_hidden: answer.is_hidden || false,
              quiz_content: answer.quiz_content || {
                question: '',
                options: ['', '', '', ''],
                correct_answer: '',
                explanation: ''
              }
            }));
          } else if (postData.content) {
            // If it's a single content piece, create one answer
            newPost.answers = [{
              content_type: 'text',
              content: postData.content,
              fileList: [],
              has_titles: false,
              is_hidden: false,
              quiz_content: {
                question: '',
                options: ['', '', '', ''],
                correct_answer: '',
                explanation: ''
              }
            }];
          }

          // Save the post
          const response = await postRequest('updatePost', newPost);
          
          if (response.data && response.data.code === 0) {
            this.$message.success('Private post created successfully! You can find it in your Posts list.');
            
            // Optionally navigate to the posts page or the edit page
            // this.$router.push('/my-posts');
            // Or to edit the newly created post:
            // if (response.data && response.data.id) {
            //   this.$router.push(`/edit-post/${response.data.id}`);
            // }
          } else {
            this.$message.error(response.data?.msg || 'Failed to create private post');
          }
        } catch (error) {
          console.error('Error creating private post:', error);
          this.$message.error('An error occurred while creating the private post');
        }
      },

      getMessageTypeFromTaskType(taskName) {
        // Map task names to message types for action buttons
        const taskTypeMap = {
          'Collection Description Polish': 'collectionDescriptionPolish',
          'Bundle Description Generation': 'bundleDescriptionGeneration', 
          'Collection Structure Generation': 'createCollectionStructure',
          'Group Knowits Suggestion': 'groupKnowitsSuggestion',
          'Post Content Generation': 'createPostContent',
          'Single Post Content': 'createSinglePostContent',
          'Post Content Improvement': 'improveSinglePostContent',
          'Custom Material': 'createPostContent',
          'Collection Cover Generation': 'createCollectionCoverPrompt',
          // Add more variations to catch different chain names
          'Collection Description Polish Chain': 'collectionDescriptionPolish',
          'Bundle Description Generation Chain': 'bundleDescriptionGeneration',
          'Collection Structure Generation Chain': 'createCollectionStructure',
          'Group Knowits Suggestion Chain': 'groupKnowitsSuggestion',
          'Post Content Generation Chain': 'createPostContent',
          'Single Post Content Chain': 'createSinglePostContent',
          'Post Content Improvement Chain': 'improveSinglePostContent',
          'Custom Material Chain': 'createPostContent',
          'Collection Cover Generation Chain': 'createCollectionCoverPrompt',
        };
        console.log('Mapping taskName:', taskName, 'to type:', taskTypeMap[taskName] || 'general');
        return taskTypeMap[taskName] || 'general';
      },

      shouldShowActionButtons(message) {
        // Show action buttons for messages that contain substantial content
        // and could potentially be used as results
        
        // If message has a structure property with chain results, always show the button
        if (message.structure) {
          console.log('Message has structure property, showing action button');
          return true;
        }
        
        if (!message.content) return false;
        
        // Check if message contains structured content, descriptions, or substantial text
        const content = message.content.toLowerCase();
        
        // Only show for results messages that explicitly contain final results
        const isResultsMessage = content.includes('<strong>final results:</strong>') || 
                                content.includes('<strong>results:</strong>') ||
                                content.includes('final results:') ||
                                content.includes('✅ ai chain completed successfully');
        
        // Check if it's a step progress message (should NOT show buttons)
        const isStepMessage = content.includes('step ') && content.includes('/');
        const isStatusMessage = content.includes('📍') || content.includes('🔄') || content.includes('❌') || content.includes('⚠️');
        const isStartingMessage = content.includes('starting ai chain') || content.includes('execute ai chain');
        const isIntermediateResult = content.includes('<em>intermediate result');
        const isCompletionMessage = content.includes('✅ ai chain completed successfully') && !message.structure;
        
        console.log('shouldShowActionButtons for message:', {
          content: content.substring(0, 100) + '...',
          hasStructure: !!message.structure,
          isResultsMessage,
          isStepMessage,
          isStatusMessage,
          shouldShow: (isResultsMessage && !isStepMessage && !isIntermediateResult && !isCompletionMessage) || !!message.structure
        });
        
        // Only show buttons for actual final results messages or messages with structure
        return isResultsMessage && !isStepMessage && !isIntermediateResult && !isStartingMessage && !isStatusMessage && !isCompletionMessage;
      },

      getActionButtonType(message) {
        // Determine what type of action button to show based on current task type or message content
        if (message.type) return message.type;
        
        // Fallback based on current context
        const currentType = this.currentTaskType;
        if (currentType === 'Collection Description Polish') return 'collectionDescriptionPolish';
        if (currentType === 'Bundle Description Generation') return 'bundleDescriptionGeneration';
        if (currentType === 'Collection Structure Generation') return 'createCollectionStructure';
        if (currentType === 'Group Knowits Suggestion') return 'groupKnowitsSuggestion';
        if (currentType === 'Post Content Generation') return 'createPostContent';
        if (currentType === 'Single Post Content') return 'createSinglePostContent';
        if (currentType === 'Post Content Improvement') return 'improveSinglePostContent';
        if (currentType === 'Custom Material') return 'createPostContent';
        if (currentType === 'Collection Cover Generation') return 'createCollectionCoverPrompt';
        
        return 'general';
      },

      startLoadingAnimation(messageIndex) {
        if (this.loadingDotsInterval) {
          clearInterval(this.loadingDotsInterval);
        }
        
        this.loadingMessageIndex = messageIndex;
        let dotCount = 0;
        const baseDots = '.';
        
        // Store the original message content
        const originalContent = this.messages[messageIndex].content;
        const baseContent = originalContent.replace(/\.+$/, ''); // Remove any existing dots
        
        this.loadingDotsInterval = setInterval(() => {
          dotCount = (dotCount % 3) + 1;
          const dots = baseDots.repeat(dotCount);
          this.messages[messageIndex].content = baseContent + dots;
        }, 500); // Update every 500ms
      },

      stopLoadingAnimation() {
        if (this.loadingDotsInterval) {
          clearInterval(this.loadingDotsInterval);
          this.loadingDotsInterval = null;
        }
        this.loadingMessageIndex = -1;
      },

      containsMarkdown(text) {
        if (!text) return false;
        
        // Check for common markdown patterns
        const markdownPatterns = [
          /^#{1,6}\s/m,        // Headers
          /\*\*[^*]+\*\*/,     // Bold
          /\*[^*]+\*/,         // Italic
          /\[.*?\]\(.*?\)/,    // Links
          /^[\*\-\+]\s/m,      // Unordered lists
          /^\d+\.\s/m,         // Ordered lists
          /^```/m,             // Code blocks
          /`[^`]+`/,           // Inline code
          /^>\s/m,             // Blockquotes
          /^\|.*\|/m,          // Tables
          /^---+$/m            // Horizontal rules
        ];
        
        return markdownPatterns.some(pattern => pattern.test(text));
      },

      renderMarkdown(text) {
        if (!text) return '';
        
        // Basic markdown to HTML conversion
        let html = text;
        
        // Escape HTML first
        html = html.replace(/&/g, '&amp;')
                   .replace(/</g, '&lt;')
                   .replace(/>/g, '&gt;');
        
        // Code blocks (must be before other replacements)
        html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
        
        // Headers
        html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
        html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
        html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
        
        // Bold
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        
        // Italic
        html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
        
        // Inline code
        html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
        
        // Links
        html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
        
        // Line breaks
        html = html.replace(/\n/g, '<br>');
        
        // Lists (basic support)
        html = html.replace(/^[\*\-\+] (.+)$/gm, '<li>$1</li>');
        html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
        
        html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
        html = html.replace(/(<li>.*<\/li>)/s, '<ol>$1</ol>');
        
        // Blockquotes
        html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');
        
        return html;
      },

      formatContextForDisplay() {
        let contextDisplay = '';

        if (this.globalContextPot && this.globalContextPot.length > 0) {
          // Show processing mode
          const mode = this.contextProcessingMode === 'batch'
            ? '<strong>Processing Mode: Batch (each item processed separately)</strong>'
            : '<strong>Processing Mode: Combined (all items as a whole)</strong>';
          contextDisplay = mode + '<br><br>';

          // Show items
          contextDisplay += this.globalContextPot.map((item, index) => {
            const label = this.getItemLabel(item);
            const preview = this.getItemPreview(item);
            return `• ${label}: ${preview}`;
          }).join('<br>');

          return contextDisplay;
        } else if (this.context_string) {
          try {
            const parsed = JSON.parse(this.context_string);
            if (typeof parsed === 'object') {
              return Object.entries(parsed).map(([key, value]) =>
                `• ${key}: ${value}`
              ).join('<br>');
            }
          } catch (e) {
            // If not JSON, just show as is
          }
          return this.context_string.substring(0, 200) + (this.context_string.length > 200 ? '...' : '');
        }
        return 'No context items';
      },

      async executeWorkflowCartSequentially(taskOperation, userAdditionalInput) {
        try {
          if (!this.workflowCart || this.workflowCart.length === 0) {
            this.messages.push({
              role: 'assistant',
              content: '❌ No workflows in cart to execute'
            });
            return;
          }

          // Check processing mode
          if (this.contextProcessingMode === 'batch' && this.globalContextPot && this.globalContextPot.length > 0) {
            // Batch mode: process each context item separately
            return await this.executeBatchMode(taskOperation, userAdditionalInput);
          } else {
            // Combined mode: process all context items together
            return await this.executeCombinedMode(taskOperation, userAdditionalInput);
          }

        } catch (error) {
          this.messages.push({
            role: 'assistant',
            content: `❌ Error during workflow cart execution: ${error.message || 'Unknown error'}`
          });

          this.$nextTick(() => {
            this.scrollToBottom();
          });

          console.error('Error with executeWorkflowCartSequentially:', error);
          throw error;
        }
      },

      async executeCombinedMode(taskOperation, userAdditionalInput) {
        // Add message about executing workflows from cart
        this.messages.push({
          role: 'assistant',
          content: `<strong>Executing ${this.workflowCart.length} workflow(s) in Combined Mode...</strong><br><em>All ${this.globalContextPot.length} context items will be processed together</em>`
        });

        let currentContext = taskOperation?.input_string || this.getContextString() || '';
        let finalResult = null;

        // Execute each workflow in the cart sequentially
        for (let i = 0; i < this.workflowCart.length; i++) {
          const workflow = this.workflowCart[i];

          this.messages.push({
            role: 'assistant',
            content: `<strong>🔄 Workflow ${i + 1}/${this.workflowCart.length}: ${workflow.name || 'Unnamed Workflow'}</strong>`
          });

          // Set current chain for execution
          this.current_chain = workflow;

          // Execute this workflow with the current context
          // Use the output from previous workflow as input for the next
          const workflowResult = await this.executeWorkflowChain(workflow, currentContext, userAdditionalInput, i === 0, taskOperation);

          if (workflowResult) {
            currentContext = workflowResult; // Use result as input for next workflow
            finalResult = workflowResult; // Keep track of final result
          }

          // Add separator between workflows except for the last one
          if (i < this.workflowCart.length - 1) {
            this.messages.push({
              role: 'assistant',
              content: `<em>Output from "${workflow.name}" will be used as input for the next workflow...</em>`
            });
          }
        }

        // Display final combined results
        if (finalResult) {
          this.messages.push({
            role: 'assistant',
            content: `✅ All workflows completed successfully!`
          });

          // Clear the workflow cart after successful execution
          this.workflowCart = [];
          this.$message.success('Workflow cart executed and cleared');
        }

        this.$nextTick(() => {
          this.scrollToBottom();
        });

        return finalResult;
      },

      async executeBatchMode(taskOperation, userAdditionalInput) {
        const contextItems = this.globalContextPot;
        const totalItems = contextItems.length;

        this.messages.push({
          role: 'assistant',
          content: `<strong>Executing in Batch Mode...</strong><br><em>Processing ${totalItems} context items separately with ${this.workflowCart.length} workflow(s) each</em>`
        });

        const batchResults = [];

        // Process each context item separately
        for (let itemIndex = 0; itemIndex < totalItems; itemIndex++) {
          const contextItem = contextItems[itemIndex];
          const itemLabel = this.getItemLabel(contextItem);

          this.messages.push({
            role: 'assistant',
            content: `<hr><strong>📦 Processing Item ${itemIndex + 1}/${totalItems}: ${itemLabel}</strong>`
          });

          // Create context string for this single item
          const singleItemContext = this.formatSingleItemContext(contextItem);
          let currentContext = singleItemContext;
          let itemResult = null;

          // Execute all workflows for this context item
          for (let workflowIndex = 0; workflowIndex < this.workflowCart.length; workflowIndex++) {
            const workflow = this.workflowCart[workflowIndex];

            this.messages.push({
              role: 'assistant',
              content: `<strong>🔄 Workflow ${workflowIndex + 1}/${this.workflowCart.length}: ${workflow.name || 'Unnamed Workflow'}</strong>`
            });

            // Set current chain for execution
            this.current_chain = workflow;

            // Execute this workflow with the current context
            const workflowResult = await this.executeWorkflowChain(
              workflow,
              currentContext,
              userAdditionalInput,
              workflowIndex === 0,
              taskOperation
            );

            if (workflowResult) {
              currentContext = workflowResult;
              itemResult = workflowResult;
            }

            // Add separator between workflows
            if (workflowIndex < this.workflowCart.length - 1) {
              this.messages.push({
                role: 'assistant',
                content: `<em>Output will be used as input for next workflow...</em>`
              });
            }
          }

          // Store result for this item
          batchResults.push({
            item: contextItem,
            label: itemLabel,
            result: itemResult
          });

          this.messages.push({
            role: 'assistant',
            content: `✅ Completed processing for: ${itemLabel}`
          });
        }

        // Display final summary
        this.messages.push({
          role: 'assistant',
          content: `<hr><strong>✅ Batch Processing Complete!</strong><br>Successfully processed ${totalItems} items`
        });

        // Clear the workflow cart after successful execution
        this.workflowCart = [];
        this.$message.success('Batch processing completed and workflow cart cleared');

        this.$nextTick(() => {
          this.scrollToBottom();
        });

        return batchResults;
      },

      formatSingleItemContext(item) {
        // Format a single context item as a string
        if (item.content) {
          return item.content;
        } else if (item.title && item.content) {
          return `${item.title}\n\n${item.content}`;
        } else if (item.title) {
          return item.title;
        } else {
          return JSON.stringify(item);
        }
      },

      async executeWorkflowChain(workflow, inputContext, userAdditionalInput, isFirstWorkflow = true, taskOperation = null) {
        try {
          const effects = workflow.effect_list || [];
          let currentContext = inputContext;
          let finalResult = null;
          const executionResults = []; // Track all step results for this workflow

          // Execute each effect in the workflow
          for (let i = 0; i < effects.length; i++) {
            const effect = effects[i];

            // Skip disabled effects
            if (effect.enabled === false) {
              console.log(`Skipping disabled effect ${i + 1}:`, effect.name || effect.type);
              this.messages.push({
                role: 'assistant',
                content: `⏭️ Step ${i + 1}/${effects.length}: ${effect.name || effect.type || 'Processing'} - Skipped (disabled)`
              });
              continue;
            }

            // Add progress message for this effect
            this.messages.push({
              role: 'assistant',
              content: `📍 Step ${i + 1}/${effects.length}: ${effect.name || effect.type || 'Processing'}...`
            });
            
            this.$nextTick(() => {
              this.scrollToBottom();
            });

            // Define additionalInput outside the try block so it's accessible in catch block
            const additionalInput = (isFirstWorkflow && i === 0) ? userAdditionalInput : '';

            try {
              // Start loading animation for this step
              this.startLoadingAnimation(this.messages.length - 1);
              
              // Create workflow-specific operation object with correct chain_id
              const workflowOperation = {
                ...(taskOperation || this.selectedTaskOperation || {}),
                chain_id: workflow._id ? (workflow._id.$oid || workflow._id.$id) : workflow.id,
                operation_name: workflow.name
              };
              
              // Execute the effect
              const effectResult = await this.executeEffect(effect, currentContext, workflowOperation, additionalInput, i);
              
              // Stop loading animation
              this.stopLoadingAnimation();
              
              // For Gemini image model, empty string is valid (image already displayed)
              const isGeminiImage = effect.model === 'gemini-3-pro-image-preview';

              if (effectResult || (effectResult === '' && isGeminiImage)) {
                // Update the step message to show completion
                this.messages[this.messages.length - 1].content = `✅ Step ${i + 1}/${effects.length}: ${effect.name || effect.type || 'Processing'} - Completed`;

                // Store step result in execution history
                executionResults.push({
                  step: i + 1,
                  name: effect.name || effect.type || `Step ${i + 1}`,
                  effect_id: effect.id || effect._id,
                  model: effect.model || 'gpt-4',
                  user_input: additionalInput || '',
                  content: currentContext || '',
                  result: effectResult,
                  res: [{
                    status: 'success',
                    code: 0,
                    msg: 'Step completed successfully',
                    data: effectResult
                  }],
                  timestamp: new Date().toISOString()
                });

                // Pass result as context to next effect
                currentContext = effectResult;
                finalResult = effectResult;
              } else {
                throw new Error(`Step ${i + 1} failed to produce result`);
              }

            } catch (stepError) {
              // Stop loading animation if error occurs
              this.stopLoadingAnimation();
              
              // Store failed step result in execution history
              executionResults.push({
                step: i + 1,
                name: effect.name || effect.type || `Step ${i + 1}`,
                effect_id: effect.id || effect._id,
                model: effect.model || 'gpt-4',
                user_input: additionalInput || '',
                content: currentContext || '',
                result: null,
                res: [{
                  status: 'error',
                  code: -1,
                  msg: stepError.message || 'Step execution failed',
                  data: null
                }],
                timestamp: new Date().toISOString()
              });
              
              this.messages.push({
                role: 'assistant',
                content: `⚠️ Step ${i + 1} encountered an error: ${stepError.message}`
              });
              
              // Decide whether to continue or stop
              if (effect.critical !== false) {
                throw stepError;
              }
            }
          }

          // Show workflow completion with result
          if (finalResult) {
            // Check if the final effect has the specific output_form for Post display
            const effects = workflow.effect_list || [];
            const lastEffect = effects[effects.length - 1];
            const shouldShowAsPost = lastEffect && lastEffect.output_form === '68baff0c94c784e2568b45e7';
            
            if (shouldShowAsPost) {
              // Parse the result and format it for the Post component
              let postData = {};
              
              try {
                if (typeof finalResult === 'string') {
                  postData = JSON.parse(finalResult);
                } else {
                  postData = finalResult;
                }
                
                // Format as a post-like object if it's not already
                if (!postData._id) {
                  // Use the answers from postData if they exist, otherwise create a single answer from the content
                  let answers = [];
                  if (postData.answers && Array.isArray(postData.answers)) {
                    answers = postData.answers;
                  } else if (postData.content) {
                    answers = [{
                      content: postData.content,
                      content_type: 'text',
                      has_title: false
                    }];
                  } else {
                    // Fallback to showing the entire result as text
                    answers = [{
                      content: typeof finalResult === 'string' ? finalResult : JSON.stringify(postData, null, 2),
                      content_type: 'text',
                      has_title: false
                    }];
                  }
                  
                  postData = {
                    id: `generated_${Date.now()}`,
                    title: postData.title || 'AI Generated Content',
                    user_name: 'AI Assistant',
                    user_id: 'ai_assistant',
                    status: 'active',
                    view_status: 'YES',
                    authorAvatar: '',
                    answers: answers,
                    thumb_up_count: 0,
                    thumb_down_count: 0,
                    mark_count: 0,
                    hidden_answers_count: 0
                  };
                }
                
                // Add results message with Post component data
                this.messages.push({
                  role: 'assistant',
                  content: '', // Empty content since we'll show the Post component
                  type: this.getMessageTypeFromTaskType(workflow.name),
                  structure: finalResult,
                  showAsPost: true,
                  postData: postData
                });
              } catch (error) {
                console.error('Error parsing result for Post display:', error);
                // Fall back to regular display
                let resultContent = '';
                let isMarkdown = false;
                
                if (typeof finalResult === 'string') {
                  resultContent = finalResult;
                  isMarkdown = this.containsMarkdown(finalResult);
                } else {
                  resultContent = JSON.stringify(finalResult, null, 2);
                }

                let formattedContent = '';
                if (isMarkdown) {
                  formattedContent = `<strong>Workflow Results:</strong><br><div class="markdown-content">${this.renderMarkdown(resultContent)}</div>`;
                } else {
                  formattedContent = `<strong>Workflow Results:</strong><br><pre>${resultContent}</pre>`;
                }

                this.messages.push({
                  role: 'assistant',
                  content: formattedContent,
                  type: this.getMessageTypeFromTaskType(workflow.name),
                  structure: finalResult
                });
              }
            } else {
              // Regular workflow result display
              let resultContent = '';
              let isMarkdown = false;
              
              if (typeof finalResult === 'string') {
                resultContent = finalResult;
                isMarkdown = this.containsMarkdown(finalResult);
              } else {
                resultContent = JSON.stringify(finalResult, null, 2);
              }

              // Format the content
              let formattedContent = '';
              if (isMarkdown) {
                formattedContent = `<strong>Workflow Results:</strong><br><div class="markdown-content">${this.renderMarkdown(resultContent)}</div>`;
              } else {
                formattedContent = `<strong>Workflow Results:</strong><br><pre>${resultContent}</pre>`;
              }

              // Add results message with appropriate action buttons
              this.messages.push({
                role: 'assistant',
                content: formattedContent,
                type: this.getMessageTypeFromTaskType(workflow.name),
                structure: finalResult
              });
            }
          }

          return finalResult;

        } catch (error) {
          console.error('Error executing workflow chain:', error);
          throw error;
        }
      },

      async executeChainWithUserInput(taskOperation, userAdditionalInput) {
        try {
          // Add AI acknowledgment message
          this.messages.push({
            role: 'assistant',
            content: `Starting AI Chain execution for "${taskOperation?.operation_name || 'AI Chain'}"...`
          });

          // Scroll to show new messages
          this.$nextTick(() => {
            this.scrollToBottom();
          });

          // Return early if chain is invalid (this was already checked but for safety)
          if (!this.current_chain || !this.current_chain.effect_list) {
            return;
          }

          const effects = this.current_chain.effect_list || [];
          // Get initial context from taskOperation or fall back to current context
          let currentContext = taskOperation?.input_string || this.getContextString() || '';
          
          // If no context available, try to get it from props or provide a default
          if (!currentContext || currentContext === '""' || currentContext === '[]' || (Array.isArray(currentContext) && currentContext.length === 0)) {
            if (this.context_string) {
              currentContext = this.context_string;
            } else if (this.globalContextPot && this.globalContextPot.length > 0) {
              currentContext = this.globalContextPot; // Pass array directly, backend will JSON encode it
            } else {
              // Provide a meaningful default context if none exists
              currentContext = `Process the following task: ${taskOperation?.operation_name || this.current_chain?.name || 'AI Chain'}`;
            }
          }
          
          let finalResult = null;
          const executionResults = []; // Track all step results for complete chain execution history

          // Debug log to understand taskOperation structure and context
          console.log('taskOperation structure:', taskOperation);
          console.log('Initial context after fallbacks:', currentContext);
          console.log('User additional input:', userAdditionalInput);

          // Execute each effect step by step
          for (let i = 0; i < effects.length; i++) {
            const effect = effects[i];

            // Skip disabled effects
            if (effect.enabled === false) {
              console.log(`Skipping disabled effect ${i + 1}:`, effect.name || effect.type);
              this.messages.push({
                role: 'assistant',
                content: `⏭️ Step ${i + 1}/${effects.length}: ${effect.name || effect.type || 'Processing'} - Skipped (disabled)`
              });
              continue;
            }

            // Add progress message for this effect
            this.messages.push({
              role: 'assistant',
              content: `📍 Step ${i + 1}/${effects.length}: ${effect.name || effect.type || 'Processing'}...`
            });
            
            this.$nextTick(() => {
              this.scrollToBottom();
            });

            try {
              // Start loading animation for this step
              this.startLoadingAnimation(this.messages.length - 1);
              
              // Execute the effect using the general model execution with user input
              const effectResult = await this.executeEffect(effect, currentContext, taskOperation, userAdditionalInput, i);
              
              // Stop loading animation
              this.stopLoadingAnimation();

              // For Gemini image model, empty string is valid (image already displayed)
              const isGeminiImage = effect.model === 'gemini-3-pro-image-preview';

              if (effectResult || (effectResult === '' && isGeminiImage)) {
                // Update the step message to show completion
                this.messages[this.messages.length - 1].content = `✅ Step ${i + 1}/${effects.length}: ${effect.name || effect.type || 'Processing'} - Completed`;

                // Store step result in execution history with complete details
                executionResults.push({
                  step: i + 1,
                  name: effect.name || effect.type || `Step ${i + 1}`,
                  effect_id: effect.id || effect._id,
                  model: effect.model || 'gpt-4',
                  user_input: userAdditionalInput || '',
                  content: currentContext || '',
                  result: effectResult,
                  res: [{
                    status: 'success',
                    code: 0,
                    msg: 'Step completed successfully',
                    data: effectResult
                  }],
                  timestamp: new Date().toISOString()
                });

                console.log(`Step ${i + 1} result stored:`, {
                  stepName: effect.name || effect.type || `Step ${i + 1}`,
                  resultLength: effectResult?.length || 0,
                  resultPreview: typeof effectResult === 'string' ? effectResult.substring(0, 100) + '...' : effectResult
                });

                // Show intermediate results if not the last step (skip for Gemini images)
                if (i < effects.length - 1 && !isGeminiImage) {
                  const previewLength = 200;
                  const preview = effectResult.length > previewLength
                    ? effectResult.substring(0, previewLength) + '...'
                    : effectResult;

                  this.messages.push({
                    role: 'assistant',
                    content: `<em>Intermediate result: ${preview}</em>`
                  });
                }

                // Pass result as context to next effect
                currentContext = effectResult;
                finalResult = effectResult;
              } else {
                throw new Error(`Step ${i + 1} failed to produce result`);
              }

            } catch (stepError) {
              // Stop loading animation if error occurs
              this.stopLoadingAnimation();
              
              // Store failed step result in execution history
              executionResults.push({
                step: i + 1,
                name: effect.name || effect.type || `Step ${i + 1}`,
                effect_id: effect.id || effect._id,
                model: effect.model || 'gpt-4',
                user_input: userAdditionalInput || '',
                content: currentContext || '',
                result: null,
                res: [{
                  status: 'error',
                  code: -1,
                  msg: stepError.message || 'Step execution failed',
                  data: null
                }],
                timestamp: new Date().toISOString()
              });

              console.log(`Step ${i + 1} failed and stored:`, {
                stepName: effect.name || effect.type || `Step ${i + 1}`,
                error: stepError.message
              });
              
              this.messages.push({
                role: 'assistant',
                content: `⚠️ Step ${i + 1} encountered an error: ${stepError.message}`
              });
              
              // Decide whether to continue or stop
              if (effect.critical !== false) {
                throw stepError;
              }
            }

            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }

          // Display final results
          if (finalResult) {
            // Check if the final result is a Base64 image (from Gemini image generation)
            const isBase64Image = typeof finalResult === 'string' && finalResult.startsWith('data:image');

            // Skip displaying final result text if it's an image (already displayed in executeEffect)
            if (!isBase64Image) {
              this.messages.push({
                role: 'assistant',
                content: `✅ AI Chain completed successfully!`
              });
            }

            // Check if the final effect has the specific output_form for Post display
            const effects = (this.current_chain && this.current_chain.effect_list) || [];
            const lastEffect = effects[effects.length - 1];
            const shouldShowAsPost = lastEffect && lastEffect.output_form === '68baff0c94c784e2568b45e7';

            if (shouldShowAsPost) {
              // Parse the result and format it for the Post component
              let postData = {};
              
              try {
                if (typeof finalResult === 'string') {
                  postData = JSON.parse(finalResult);
                } else {
                  postData = finalResult;
                }
                
                // Format as a post-like object if it's not already
                if (!postData._id) {
                  // Use the answers from postData if they exist, otherwise create a single answer from the content
                  let answers = [];
                  if (postData.answers && Array.isArray(postData.answers)) {
                    answers = postData.answers;
                  } else if (postData.content) {
                    answers = [{
                      content: postData.content,
                      content_type: 'text',
                      has_title: false
                    }];
                  } else {
                    // Fallback to showing the entire result as text
                    answers = [{
                      content: typeof finalResult === 'string' ? finalResult : JSON.stringify(postData, null, 2),
                      content_type: 'text',
                      has_title: false
                    }];
                  }
                  
                  postData = {
                    id: `generated_${Date.now()}`,
                    title: postData.title || 'AI Generated Content',
                    user_name: 'AI Assistant',
                    user_id: 'ai_assistant',
                    status: 'active',
                    view_status: 'YES',
                    authorAvatar: '',
                    answers: answers,
                    thumb_up_count: 0,
                    thumb_down_count: 0,
                    mark_count: 0,
                    hidden_answers_count: 0
                  };
                }
                
                // Add results message with Post component data
                this.messages.push({
                  role: 'assistant',
                  content: '', // Empty content since we'll show the Post component
                  type: this.getMessageTypeFromTaskType((taskOperation && taskOperation.operation_name) || (this.current_chain && this.current_chain.name)),
                  structure: finalResult,
                  showAsPost: true,
                  postData: postData
                });
              } catch (error) {
                console.error('Error parsing result for Post display:', error);
                // Fall back to regular display
                let resultContent = '';
                let isMarkdown = false;
                
                if (typeof finalResult === 'string') {
                  resultContent = finalResult;
                  isMarkdown = this.containsMarkdown(finalResult);
                } else {
                  resultContent = JSON.stringify(finalResult, null, 2);
                }

                let formattedContent = '';
                if (isMarkdown) {
                  formattedContent = `<strong>Final Results:</strong><br><div class="markdown-content">${this.renderMarkdown(resultContent)}</div>`;
                } else {
                  formattedContent = `<strong>Final Results:</strong><br><pre>${resultContent}</pre>`;
                }

                this.messages.push({
                  role: 'assistant',
                  content: formattedContent,
                  type: this.getMessageTypeFromTaskType(taskOperation?.operation_name || this.current_chain?.name),
                  structure: finalResult
                });
              }
            } else if (!isBase64Image) {
              // Regular chain result display (skip for Base64 images)
              let resultContent = '';
              let isMarkdown = false;

              if (typeof finalResult === 'string') {
                resultContent = finalResult;
                // Check if the content contains markdown indicators
                isMarkdown = this.containsMarkdown(finalResult);
              } else {
                resultContent = JSON.stringify(finalResult, null, 2);
              }

              // Format the content based on whether it's markdown or not
              let formattedContent = '';
              if (isMarkdown) {
                // Render as markdown
                formattedContent = `<strong>Final Results:</strong><br><div class="markdown-content">${this.renderMarkdown(resultContent)}</div>`;
              } else {
                // Render as preformatted text
                formattedContent = `<strong>Final Results:</strong><br><pre>${resultContent}</pre>`;
              }

              // Add the final results message with action buttons
              this.messages.push({
                role: 'assistant',
                content: formattedContent,
                type: this.getMessageTypeFromTaskType(taskOperation?.operation_name || this.current_chain?.name),
                structure: finalResult
              });
            }

            // Update task operation status with complete execution data
            if (taskOperation && (taskOperation._id || taskOperation.id)) {
              const operationId = taskOperation._id?.$id || taskOperation._id?.$oid || taskOperation._id || taskOperation.id;
              
              console.log('Updating task operation with complete results:', {
                operationId,
                executionStepsCount: executionResults.length,
                finalResultLength: finalResult?.length || 0
              });
              
              await postRequest('updateTaskOperation', {
                id: operationId,
                status: 'completed',
                result: finalResult,
                exe_result: executionResults,      // Include ALL step results
                final_results: [finalResult],      // Include final results array as expected by Task.vue
                start_input: taskOperation?.input_string || this.getContextString() || ''
              });
            }
          }

          this.$nextTick(() => {
            this.scrollToBottom();
          });

          return finalResult;

        } catch (error) {
          // Add error message to dialogue
          this.messages.push({
            role: 'assistant',
            content: `❌ Error occurred during AI Chain execution: ${error.message || 'Unknown error'}`
          });
          
          this.$nextTick(() => {
            this.scrollToBottom();
          });
          
          console.error('Error with executeChainWithUserInput:', error);
          throw error;
        }
      },

      async toEditImage(action, index, taskId) {
        let result = await this.exeMidJModelChange(action, index, taskId);
        if (result) {
          // Find the message with the matching taskId and update it
          for (let i = this.messages.length - 1; i >= 0; i--) {
            if (this.messages[i].taskId === taskId) {
              this.$set(this.messages[i], 'imgUrl', result.url);
              this.$set(this.messages[i], 'taskId', result.taskId);
              this.$set(this.messages[i], 'action', result.action);
              break;
            }
          }
        }
      },

      exeMidJModelChange(action, index, taskId) {
        return new Promise((resolve) => {
          this.isLoading = true;
          postRequestAISG("changeImage", {
            action,
            index,
            taskId,
          }).then(async (res) => {
            this.isLoading = false;
            let result = res.data.data;
            if (result.code === 1) {
              this.progress = "0%";
              
              // Add a new message for the new image generation
              this.messages.push({
                role: "assistant",
                content: `Current progress in image generation: ${this.progress}`,
                type: "createCollectionCoverPrompt",
                imgUrl: "",
                taskId: "",
                action: ""
              });
              this.scrollToBottom();
              
              let messageId = res.data.data.result;
              let url = await this.getResult(messageId);
              if (url) {
                resolve({
                  taskId: messageId,
                  url: url.url,
                  action: url.action
                });
              }
            } else {
              this.$message.error(result.description);
              resolve(false);
            }
          });
        });
      },

      exeMidJModel(prompt) {
        return new Promise((resolve, reject) => {
          this.isLoading = true;
          console.log('Calling getMidJourneyNew with prompt:', prompt);
          postRequestAISG("getMidJourneyNew", {
            prompt,
          }).then(async (res) => {
            console.log('getMidJourneyNew response:', res);
            console.log('Response structure:', {
              hasRes: !!res,
              hasData: !!res?.data,
              code: res?.data?.code,
              hasDataData: !!res?.data?.data,
              dataData: res?.data?.data,
              result: res?.data?.data?.result,
              fullResponse: JSON.stringify(res)
            });
            this.isLoading = false;
            this.progress = "0%";

            // Check different possible response structures
            let messageId = null;

            if (res && res.data) {
              if (res.data.code === 0) {
                // Try different possible locations for the messageId/taskId
                messageId = res.data.data?.result ||
                           res.data.data?.taskId ||
                           res.data.data?.message_id ||
                           res.data.result ||
                           res.data.taskId;
              }
            }

            if (messageId) {
              console.log('Starting progress tracking for messageId:', messageId);
              let url = await this.getResult(messageId);
              if (url) {
                resolve({
                  taskId: messageId,
                  url: url.url,
                  action: url.action
                });
              } else {
                reject(new Error('Failed to get result from Midjourney'));
              }
            } else {
              console.error('Could not find messageId in response:', res);
              console.error('Response data:', res?.data);
              this.isLoading = false;
              reject(new Error(res?.data?.msg || res?.data?.message || 'Failed to initiate Midjourney generation - no messageId returned'));
            }
          }).catch(error => {
            console.error('Error calling getMidJourneyNew:', error);
            this.isLoading = false;
            reject(error);
          });
        });
      },

      getResult(messageId) {
        console.log('getResult called with messageId:', messageId);
        let flag = false;
        return new Promise((resolve) => {
          let timer = setInterval(() => {
            if (!flag) {
              flag = true;
              console.log('Polling getMidJourneyResultNew for taskId:', messageId);
              postRequestAISG("getMidJourneyResultNew", {
                taskId: messageId,
              }).then((res) => {
                console.log('getMidJourneyResultNew response:', res);
                if (res.data.code === 0) {
                  flag = false;
                  let result = res.data.data;
                  console.log('Progress update:', result.progress, 'Status:', result.status);

                  // Update progress in the most recent message with matching taskId or type
                  for (let i = this.messages.length - 1; i >= 0; i--) {
                    const message = this.messages[i];
                    if (message.type === "createCollectionCoverPrompt" &&
                        (!message.imgUrl || message.taskId === messageId)) {
                      this.$set(message, "content", `Current progress in image generation: ${result.progress}`);
                      this.$set(message, "taskId", messageId);
                      break;
                    }
                  }

                  if (result.status === "SUCCESS") {
                    console.log('Image generation complete! URL:', result.image_qiniu_url);
                    clearInterval(timer);
                    resolve({
                      url: result.image_qiniu_url,
                      action: result.action
                    });
                  }
                } else {
                  console.error('Error in getMidJourneyResultNew:', res.data);
                  clearInterval(timer);
                  resolve();
                }
              }).catch(error => {
                console.error('Error calling getMidJourneyResultNew:', error);
                flag = false;
              });
            }
          }, 3000);
        });
      },

      useImageAsCover(imageUrl) {
        this.$emit('use', imageUrl);
      },

      async regenerateGeminiImage(message) {
        try {
          if (!message.geminiRegenInfo) {
            this.$message.error('Cannot regenerate: missing regeneration info');
            return;
          }

          // Set regenerating flag to show loading state
          this.$set(message, 'regenerating', true);

          console.log('Regenerating Gemini image with info:', message.geminiRegenInfo);

          const { effect, inputContent, taskOperation, userAdditionalInput, effectIndex } = message.geminiRegenInfo;

          // Call executeEffect again with the same parameters
          const newImageDataUrl = await this.executeEffect(
            effect,
            inputContent,
            taskOperation,
            userAdditionalInput,
            effectIndex
          );

          // Update the existing message with the new image
          // Note: executeEffect already creates a new message, so we need to remove the duplicate
          // and update the original message instead

          // Remove the newly created message (it's the last one)
          if (this.messages.length > 0 && this.messages[this.messages.length - 1].imgUrl) {
            const newMessage = this.messages.pop();

            // Update the original message with the new image
            this.$set(message, 'imgUrl', newMessage.imgUrl);
          }

          this.$set(message, 'regenerating', false);
          this.$message.success('Image regenerated successfully!');

        } catch (error) {
          console.error('Error regenerating Gemini image:', error);
          this.$set(message, 'regenerating', false);
          this.$message.error('Failed to regenerate image: ' + (error.message || 'Unknown error'));
        }
      },

      // Workflow methods
      async loadWorkflows() {
        try {
          this.loadingWorkflows = true;
          
          // Load user's own workflows
          const myWorkflowsResponse = await postRequest('getChainList', {
            user_id: getUserId()
          });
          
          if (myWorkflowsResponse && myWorkflowsResponse.data && myWorkflowsResponse.data.code === 0) {
            this.myWorkflows = myWorkflowsResponse.data.data || [];
            // Keep backwards compatibility
            this.workflows = this.myWorkflows;
          }

          // Load public workflows built by others using the correct API
          const publicWorkflowsResponse = await postRequest('getPublicWorkflows', {
            user_id: getUserId()
          });
          
          if (publicWorkflowsResponse && publicWorkflowsResponse.data && publicWorkflowsResponse.data.code === 0) {
            this.publicWorkflows = publicWorkflowsResponse.data.data || [];
          } else {
            this.publicWorkflows = [];
          }
          
        } catch (error) {
          console.error('Error loading workflows:', error);
          this.$message.error('Failed to load workflows');
        } finally {
          this.loadingWorkflows = false;
        }
      },
      
      selectWorkflow(workflow) {
        this.selectedWorkflow = workflow;
      },
      
      isWorkflowSelected(workflow) {
        if (!this.selectedWorkflow) return false;
        const workflowId = workflow._id ? (workflow._id.$oid || workflow._id.$id) : workflow.id;
        const selectedId = this.selectedWorkflow._id ? (selectedWorkflow._id.$oid || selectedWorkflow._id.$id) : this.selectedWorkflow.id;
        return workflowId === selectedId;
      },
      
      addToWorkflowCart(workflow) {
        // Check if workflow is already in cart
        if (!this.isWorkflowInCart(workflow)) {
          this.workflowCart.push(workflow);
          this.$message.success(`Added "${workflow.name}" to workflow cart`);
        }
      },
      
      isWorkflowInCart(workflow) {
        const workflowId = workflow._id ? (workflow._id.$oid || workflow._id.$id) : workflow.id;
        return this.workflowCart.some(w => {
          const cartId = w._id ? (w._id.$oid || w._id.$id) : w.id;
          return cartId === workflowId;
        });
      },
      
      removeFromWorkflowCart(workflow) {
        if (typeof workflow === 'number') {
          // If index is passed
          this.workflowCart.splice(workflow, 1);
        } else {
          // If workflow object is passed
          const workflowId = workflow._id ? (workflow._id.$oid || workflow._id.$id) : workflow.id;
          const index = this.workflowCart.findIndex(w => {
            const cartId = w._id ? (w._id.$oid || w._id.$id) : w.id;
            return cartId === workflowId;
          });
          if (index !== -1) {
            this.workflowCart.splice(index, 1);
          }
        }
      },
      
      removeWorkflowFromCart(index) {
        const workflow = this.workflowCart[index];
        this.workflowCart.splice(index, 1);
        this.$message.info(`Removed "${workflow.name}" from workflow cart`);
      },
      
      clearWorkflowCart() {
        this.workflowCart = [];
        this.$message.info('Workflow cart cleared');
      },
      
      getEffectCount(workflow) {
        if (!workflow || !workflow.effect_list) return 0;
        return workflow.effect_list.length;
      },
      
      async executeWorkflowFromCart(workflow) {
        try {
          // Check if we want to execute all workflows in cart or just this one
          // If there are multiple workflows in cart, show option to execute all sequentially
          if (this.workflowCart.length > 1) {
            // Execute all workflows in cart sequentially
            await this.executeAllWorkflowsInCart();
          } else {
            // Execute single workflow
            this.current_chain = workflow;
            
            // Create a new task operation for this workflow
            const contextString = this.getContextString();
            
            const operationResponse = await postRequest('updateTaskOperation', {
              task_id: this.localTaskId || '',
              operation_type: 'chain',
              chain_id: workflow._id ? (workflow._id.$oid || workflow._id.$id) : workflow.id,
              operation_name: workflow.name,
              user_id: getUserId(),
              input_string: contextString,
              status: 'pending'
            });
            
            if (operationResponse && operationResponse.data && operationResponse.data.code === 0) {
              this.selectedTaskOperation = operationResponse.data.data;
              
              // Switch to messages view if in workflows tab
              if (this.activeTab === 'workflows') {
                this.activeTab = 'tasks';
              }
              
              // Execute the workflow
              await this.queryAIChain(this.selectedTaskOperation);
            }
          }
        } catch (error) {
          console.error('Error executing workflow:', error);
          this.$message.error('Failed to execute workflow');
        }
      },
      
      async executeAllWorkflowsInCart(userInstructions = '') {
        try {
          // Create task if needed
          if (!this.localTaskId || this.localTaskId === '') {
            // Create task title using context and first workflow name
            let taskTitle = 'Workflow Cart Execution';
            
            if (this.workflowCart.length > 0) {
              const firstWorkflow = this.workflowCart[0];
              
              // Extract context content name if available
              let contextName = '';
              if (this.context_pot && this.context_pot.length > 0) {
                const firstContext = this.context_pot[0];
                if (typeof firstContext === 'string') {
                  try {
                    const parsed = JSON.parse(firstContext);
                    contextName = parsed.collection_name || parsed.name || parsed.title || '';
                  } catch (e) {
                    contextName = '';
                  }
                } else {
                  contextName = firstContext.collection_name || firstContext.name || firstContext.title || '';
                }
              } else if (this.context_string) {
                try {
                  const parsed = JSON.parse(this.context_string);
                  contextName = parsed.collection_name || parsed.name || parsed.title || '';
                } catch (e) {
                  contextName = '';
                }
              }
              
              // Concatenate context name and first workflow name
              if (contextName) {
                taskTitle = `${contextName} - ${firstWorkflow.name || 'Workflow'}`;
              } else {
                taskTitle = `${firstWorkflow.name || 'Workflow'} (${this.workflowCart.length} workflows)`;
              }
            }
            
            const taskResponse = await postRequest('updateTask', {
              id: '',
              user_id: getUserId(),
              title: taskTitle,
              description: `Sequential execution of ${this.workflowCart.length} workflows`,
              operations: []
            });
            
            if (taskResponse && taskResponse.data && taskResponse.data.code === 0) {
              this.tasks.push(taskResponse.data.data);
              this.localTaskId = (taskResponse.data.data._id.$oid || taskResponse.data.data._id.$id);
            }
          }
          
          // Create task operations for each workflow in the cart
          const contextString = this.getContextString();
          const createdOperations = [];
          
          // Create a TaskOperation for each workflow in the cart
          for (let i = 0; i < this.workflowCart.length; i++) {
            const workflow = this.workflowCart[i];
            
            const operationResponse = await postRequest('updateTaskOperation', {
              task_id: this.localTaskId,
              operation_type: 'chain',
              chain_id: workflow._id ? (workflow._id.$oid || workflow._id.$id) : workflow.id,
              operation_name: workflow.name || `Workflow ${i + 1}`,
              user_id: getUserId(),
              input_string: contextString,
              status: 'pending'
            });
            
            if (operationResponse && operationResponse.data && operationResponse.data.code === 0) {
              createdOperations.push(operationResponse.data.data);
            }
          }
          
          if (createdOperations.length > 0) {
            // Use the first operation as the selected one for UI purposes
            this.selectedTaskOperation = createdOperations[0];
            
            // Switch to messages view if in workflows tab
            if (this.activeTab === 'workflows') {
              this.activeTab = 'tasks';
            }
            
            // Execute all workflows in cart sequentially
            for (let i = 0; i < createdOperations.length; i++) {
              const operation = createdOperations[i];
              const isFirst = i === 0;
              
              // Execute each workflow operation
              await this.queryAIChain(operation, true, isFirst ? userInstructions : '');
            }
          }
        } catch (error) {
          console.error('Error executing workflow cart:', error);
          this.$message.error('Failed to execute workflow cart');
        }
      },
      
    },
    beforeDestroy() {
      // Clean up the loading animation interval when component is destroyed
      if (this.loadingDotsInterval) {
        clearInterval(this.loadingDotsInterval);
      }
    }
  };
  </script>
    
  <style scoped>
  /* AI Task Analysis Styles */
  >>> .ai-task-analysis {
    margin: 15px 0;
    padding: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
  }
  
  >>> .ai-task-analysis .analysis-header {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  >>> .ai-task-analysis .analysis-header h3 {
    color: white;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }
  
  >>> .ai-task-analysis .analysis-header p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    margin: 0;
  }
  
  >>> .subtasks-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  >>> .subtask-card {
    background: white;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  
  >>> .subtask-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  >>> .subtask-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }
  
  >>> .subtask-number {
    background: #667eea;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    flex-shrink: 0;
  }
  
  >>> .subtask-status {
    font-size: 18px;
    flex-shrink: 0;
  }
  
  >>> .subtask-title {
    font-weight: 600;
    color: #2d3748;
    font-size: 15px;
    flex: 1;
  }
  
  >>> .subtask-time {
    background: #f0f4f8;
    color: #4a5568;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 12px;
    white-space: nowrap;
  }
  
  >>> .subtask-description {
    color: #4a5568;
    font-size: 14px;
    line-height: 1.5;
    margin-left: 34px;
    padding: 8px 0;
  }
  
  >>> .subtask-dependencies {
    color: #718096;
    font-size: 12px;
    margin-left: 34px;
    padding: 5px 0;
    font-style: italic;
  }
  
  >>> .subtask-actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  >>> .action-btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;
  }
  
  >>> .action-btn.primary {
    background: white;
    color: #667eea;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  >>> .action-btn.primary:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  }
  
  >>> .action-btn.secondary {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
  
  >>> .action-btn.secondary:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  /* Embedded Subtasks Styles */
  >>> .embedded-subtasks {
    margin: 15px 0;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }
  
  >>> .embedded-subtasks h4 {
    color: #2d3748;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 12px 0;
  }
  
  >>> .subtasks-list {
    list-style: none;
    padding: 0;
    margin: 0;
    counter-reset: subtask-counter;
  }
  
  >>> .subtask-item {
    padding: 12px;
    margin-bottom: 10px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
    counter-increment: subtask-counter;
  }
  
  >>> .subtask-item:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
  
  >>> .subtask-main {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  >>> .status-icon {
    font-size: 16px;
  }
  
  >>> .time-badge {
    background: #edf2f7;
    color: #4a5568;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 11px;
    margin-left: 8px;
  }
  
  >>> .subtask-desc {
    color: #718096;
    font-size: 13px;
    line-height: 1.4;
    margin-top: 8px;
    padding-left: 24px;
  }
  
  /* Legacy Formatted subtasks display styles */
  .formatted-subtasks {
    margin: 10px 0;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }
  
  .formatted-subtasks h4 {
    margin: 0 0 12px 0;
    color: #2d3748;
    font-size: 16px;
    font-weight: 600;
  }
  
  .subtasks-display-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  .subtask-display-item {
    padding: 12px;
    margin-bottom: 8px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
  }
  
  .subtask-display-item:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .subtask-display-item:last-child {
    margin-bottom: 0;
  }
  
  .subtask-status {
    display: inline-block;
    margin-right: 8px;
    font-size: 16px;
  }
  
  .subtask-title {
    font-weight: 500;
    color: #2d3748;
    font-size: 14px;
  }
  
  .subtask-time {
    color: #718096;
    font-size: 12px;
    margin-left: 8px;
  }
  
  .subtask-desc {
    display: block;
    margin-top: 4px;
    color: #4a5568;
    font-size: 13px;
    line-height: 1.5;
    padding-left: 28px;
  }
  
  .ai-dialogue {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: white;
    overflow: hidden;
  }

  .dialogue-content {
    display: flex;
    flex: 1;
    height: 100%;
    gap: 1px;
    overflow: hidden;
    min-width: 0;
    box-sizing: border-box;
  }

  .results-preview {
    width: 50%;
    flex: 0 0 50%;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #ebeef5;
    background-color: #f8f9fa;
    overflow: hidden;
    min-width: 0;
    max-width: 50%;
    box-sizing: border-box;
  }

  .preview-header {
    padding: 15px;
    border-bottom: 1px solid #ebeef5;
    background-color: white;
  }

  .preview-header h3 {
    margin: 0;
    color: #303133;
  }

  .preview-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
  }

  .preview-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #909399;
    text-align: center;
  }

  .dialogue-section {
    width: 50%;
    flex: 0 0 50%;
    display: flex;
    flex-direction: column;
    background-color: white;
    min-width: 0;
    max-width: 50%;
    overflow: hidden;
    box-sizing: border-box;
  }

  .dialogue-section.full-width {
    width: 100%;
    flex: 0 0 100%;
    max-width: 100%;
  }

  .dialogue-header {
    padding: 15px;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .dialogue-header h3 {
    margin: 0;
    color: #303133;
    flex-shrink: 0;
  }

  .dialogue-header .el-button {
    flex-shrink: 0;
  }

  .dialogue-header h3 {
    margin: 0;
    color: #303133;
  }

  .messages-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
    min-width: 0;
  }

  .message {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
  }

  .message.user {
    align-items: flex-end;
  }

  .message.assistant {
    align-items: flex-start;
  }

  .message-content {
    max-width: 100%;
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    word-break: break-word;
    overflow-wrap: break-word;
    box-sizing: border-box;
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
  }

  .user .message-content {
    background-color: #409eff;
    color: white;
    max-width: 80%;
    width: auto;
  }

  .assistant .message-content {
    background-color: #f5f7fa;
    color: #303133;
  }

  /* Post message styling */
  .post-message {
    max-width: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 8px 0;
  }

  .post-message :deep(.post-card) {
    max-width: 400px;
    margin: 0;
  }

  .input-area {
    padding: 15px;
    border-top: 1px solid #ebeef5;
  }

  .input-area .el-button {
    width: 100%;
    margin-top: 10px;
  }

  .loading-dots {
    display: flex;
    gap: 4px;
    padding: 12px 16px;
    background-color: #f5f7fa;
    border-radius: 8px;
  }

  .loading-dots span {
    width: 8px;
    height: 8px;
    background-color: #909399;
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out;
  }

  .loading-dots span:nth-child(1) {
    animation-delay: 0s;
  }
  .loading-dots span:nth-child(2) {
    animation-delay: 0.16s;
  }
  .loading-dots span:nth-child(3) {
    animation-delay: 0.32s;
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }

  :deep(.message-content) {
    line-height: 1.5;
    overflow-wrap: break-word;
    word-break: break-word;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
  }

  :deep(.message-content p) {
    margin: 0;
  }

  :deep(.message-content pre) {
    background-color: #f8f9fa;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    white-space: pre-wrap;
    max-width: 100%;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  :deep(.user .message-content pre) {
    background-color: rgba(255, 255, 255, 0.1);
  }

  /* Context Cart Styles */
  .context-cart {
    margin: 15px;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    background-color: #f8f9fa;
    overflow: hidden;
  }

  .cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #ecf5ff;
    border-bottom: 1px solid #e4e7ed;
  }
  
  .cart-header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .cart-header h4 {
    margin: 0;
    color: #409eff;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .processing-mode-selector {
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .processing-mode-selector :deep(.el-radio-button__inner) {
    padding: 6px 12px;
    font-size: 12px;
    border: 1px solid #dcdfe6;
  }

  .processing-mode-selector :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
    background-color: #409eff;
    border-color: #409eff;
    color: white;
  }

  .processing-mode-selector span i {
    margin-right: 4px;
  }

  .clear-cart-btn {
    padding: 4px 8px;
    color: #f56c6c;
  }

  .clear-cart-btn:hover {
    color: #f78989;
  }

  .cart-items {
    max-height: 200px;
    overflow-y: auto;
  }

  .cart-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    transition: background-color 0.2s;
  }

  .cart-item:last-child {
    border-bottom: none;
  }

  .cart-item:hover {
    background-color: #f0f2f5;
  }

  .item-content {
    flex: 1;
  }

  .item-label {
    font-weight: 500;
    color: #303133;
    font-size: 13px;
    margin-bottom: 4px;
  }

  .item-preview {
    color: #909399;
    font-size: 12px;
    line-height: 1.4;
  }

  .remove-item-btn {
    color: #c0c4cc;
    padding: 4px;
    margin-left: 8px;
  }

  .remove-item-btn:hover {
    color: #f56c6c;
  }

  /* Empty Cart State */
  .context-cart.empty-cart {
    opacity: 0.8;
  }

  .empty-cart-message {
    padding: 20px 16px;
    text-align: center;
    color: #909399;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .empty-cart-message i {
    font-size: 16px;
    color: #c0c4cc;
  }

  .empty-cart-message span {
    font-style: italic;
  }

  /* Assistant action buttons */
  .assistant-actions {
    margin-top: 10px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .improvement-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  /* Force break long text content */
  :deep(.message-content *) {
    max-width: 100%;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  /* Specific handling for strong tags and other inline elements */
  :deep(.message-content strong) {
    word-break: break-word;
    overflow-wrap: break-word;
  }

  /* Markdown content styling */
  :deep(.markdown-content) {
    background-color: #f8f9fa;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 16px;
    margin-top: 8px;
    overflow-x: auto;
  }

  :deep(.markdown-content h1) {
    font-size: 1.8em;
    margin: 0.5em 0;
    color: #303133;
  }

  :deep(.markdown-content h2) {
    font-size: 1.5em;
    margin: 0.5em 0;
    color: #303133;
  }

  :deep(.markdown-content h3) {
    font-size: 1.2em;
    margin: 0.5em 0;
    color: #303133;
  }

  :deep(.markdown-content code) {
    background-color: #f0f2f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }

  :deep(.markdown-content pre) {
    background-color: #2d2d2d;
    color: #f8f8f2;
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 10px 0;
  }

  :deep(.markdown-content pre code) {
    background-color: transparent;
    padding: 0;
    color: inherit;
  }

  :deep(.markdown-content blockquote) {
    border-left: 4px solid #409eff;
    padding-left: 16px;
    margin: 10px 0;
    color: #606266;
    font-style: italic;
  }

  :deep(.markdown-content ul),
  :deep(.markdown-content ol) {
    margin: 10px 0;
    padding-left: 24px;
  }

  :deep(.markdown-content li) {
    margin: 4px 0;
  }

  :deep(.markdown-content a) {
    color: #409eff;
    text-decoration: none;
  }

  :deep(.markdown-content a:hover) {
    text-decoration: underline;
  }

  :deep(.markdown-content table) {
    border-collapse: collapse;
    width: 100%;
    margin: 10px 0;
  }

  :deep(.markdown-content th),
  :deep(.markdown-content td) {
    border: 1px solid #dcdfe6;
    padding: 8px;
    text-align: left;
  }

  :deep(.markdown-content th) {
    background-color: #f5f7fa;
    font-weight: bold;
  }

  /* Ensure columns maintain 50/50 split even with long content */
  .dialogue-content > * {
    flex-shrink: 0;
    flex-grow: 0;
  }

  /* Force message content to wrap and not overflow */
  .message-content,
  :deep(.message-content) {
    max-width: 100% !important;
    width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    word-wrap: break-word !important;
    hyphens: auto;
  }

  /* Ensure long URLs and code don't break layout */
  .message-content *,
  :deep(.message-content *) {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
  }

  /* Handle very long content in pre/code blocks */
  .message-content pre,
  .message-content code,
  :deep(.message-content pre),
  :deep(.message-content code) {
    white-space: pre-wrap !important;
    word-break: break-all !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }

  /* Midjourney Image Styles */
  .midjourney-image {
    margin-top: 10px;
  }

  .image-edit-buttons {
    margin-top: 10px;
  }

  .button-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }

  .button-row:last-child {
    margin-bottom: 0;
  }

  .cover-action {
    margin-top: 10px;
    display: flex;
    justify-content: center;
  }

  .gemini-image-actions {
    margin-top: 10px;
    display: flex;
    justify-content: center;
  }

  .midjourney-image img {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
  }

  .midjourney-image img:hover {
    transform: scale(1.02);
  }

  .image-edit-buttons .el-button {
    min-width: 40px;
  }
  
  /* Tab Bar Styles */
  .tab-bar {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .result-tabs {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .result-tabs >>> .el-tabs__header {
    margin-bottom: 0;
    border-bottom: 1px solid #e4e7ed;
    background-color: white;
    padding: 0 20px;
  }
  
  .result-tabs >>> .el-tabs__content {
    flex: 1;
    padding: 0;
    overflow: hidden;
  }
  
  .result-tabs >>> .el-tab-pane {
    height: 100%;
  }
  
  /* Workflow Sub Tabs */
  .workflow-sub-tabs {
    padding: 15px 20px;
    background-color: #fff;
    border-bottom: 1px solid #e8e8e8;
  }

  .workflow-radio-group {
    display: flex;
    gap: 10px;
  }

  .workflow-radio-group ::v-deep .el-radio-button__inner {
    padding: 8px 20px;
    border-radius: 20px;
    border: 1px solid #e0e0e0;
    background: #f5f5f5;
    color: #666;
    font-weight: 500;
  }

  .workflow-radio-group ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: transparent;
    color: white;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }

  .workflow-radio-group ::v-deep .el-radio-button__inner:hover {
    border-color: #667eea;
    color: #667eea;
  }

  /* Workflow List Styles */
  .workflow-list {
    height: calc(100% - 60px);
    overflow-y: auto;
    padding: 20px;
    background-color: #f8f9fa;
  }
  
  .loading-workflows {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #909399;
    font-size: 14px;
  }
  
  .loading-workflows i {
    font-size: 32px;
    margin-bottom: 10px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .empty-workflows {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #909399;
  }
  
  .empty-workflows i {
    font-size: 48px;
    margin-bottom: 10px;
    color: #c0c4cc;
  }
  
  .workflow-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .workflow-card {
    background: white;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 15px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 15px;
  }
  
  .workflow-card:hover {
    border-color: #409eff;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
  }
  
  .workflow-card.selected {
    border-color: #409eff;
    background-color: #ecf5ff;
  }
  
  .workflow-icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    color: white;
    font-size: 24px;
  }
  
  .workflow-info {
    flex: 1;
    min-width: 0;
  }
  
  .workflow-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 5px;
  }
  
  .workflow-info h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    margin-right: 8px;
  }
  
  .workflow-visibility-badge {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
  }
  
  .workflow-visibility-badge i {
    margin-right: 2px;
  }
  
  .workflow-info p {
    margin: 0 0 8px 0;
    font-size: 13px;
    color: #909399;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  
  .workflow-meta {
    display: flex;
    gap: 15px;
    font-size: 12px;
    color: #606266;
  }
  
  .workflow-meta .effect-count {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .workflow-actions {
    flex-shrink: 0;
  }
  
  /* Workflow Cart Styles */
  .workflow-cart {
    margin: 15px;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    background-color: #f5f3ff;
    overflow: hidden;
  }
  
  .workflow-cart .cart-header {
    background-color: #e6e2ff;
    border-bottom: 1px solid #d8d0ff;
  }
  
  .workflow-cart .cart-header h4 {
    color: #7c3aed;
  }
  
  .workflow-cart-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    transition: background-color 0.2s;
  }
  
  .workflow-cart-item:last-child {
    border-bottom: none;
  }
  
  .workflow-cart-item:hover {
    background-color: #f9f7ff;
  }
  
  .workflow-cart-content {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
  }
  
  .workflow-cart-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 6px;
    color: white;
    font-size: 16px;
  }
  
  .workflow-cart-info {
    flex: 1;
    min-width: 0;
  }
  
  .workflow-cart-name {
    font-weight: 500;
    color: #303133;
    font-size: 13px;
    margin-bottom: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .workflow-cart-meta {
    color: #909399;
    font-size: 11px;
  }
  
  .workflow-cart-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  /* Subtasks Display Styles */
  .subtasks-result {
    background: #f0f9ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 20px;
    margin: 10px 0;
  }
  
  .subtasks-result h3 {
    color: #1e40af;
    margin: 0 0 12px 0;
    font-size: 18px;
  }
  
  .subtasks-result p {
    color: #64748b;
    margin: 0 0 16px 0;
  }
  
  .subtasks-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }
  
  .subtask-item {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px;
    transition: all 0.2s;
  }
  
  .subtask-item:hover {
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .subtask-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }
  
  .subtask-number {
    background: #3b82f6;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 12px;
    flex-shrink: 0;
  }
  
  .subtask-title {
    font-weight: 600;
    color: #1e293b;
    flex: 1;
  }
  
  .subtask-time {
    color: #64748b;
    font-size: 12px;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 4px;
  }
  
  .subtask-description {
    color: #475569;
    font-size: 14px;
    line-height: 1.5;
    padding-left: 34px;
  }
  
  .subtask-deps {
    color: #94a3b8;
    font-size: 12px;
    padding-left: 34px;
    margin-top: 6px;
    font-style: italic;
  }
  
  .subtask-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
  }
  
  .add-subtasks-btn {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .add-subtasks-btn:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  }
  
  .add-subtasks-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
  </style>